import React, { useState, useEffect, useRef } from 'react';
import AV from 'leancloud-storage/live-query';
// 引入 Agora SDK
import AgoraRTC from "agora-rtc-sdk-ng"; 
import { 
  BookOpen, Bot, X, Settings, Activity, CreditCard, ChevronUp, ChevronDown, 
  Menu, User, RefreshCw, LogOut, MessageCircle, Send, Plus, LogIn, Home, Power, Layers, 
  Trophy, Eye, Coins, Mic, MicOff, Volume2, Music, Speaker
} from 'lucide-react';

// --- 1. LeanCloud 初始化 ---
AV.init({
  appId: "P9uxAdpiX0ftwD2nZi7KKEDo-MdYXbMMI",
  appKey: "20HZrwSY5Bv5LdGBgr9jyhXF",
  serverURL: "https://studygame.top/lc-api" 
});

// --- 2. 常量定义 ---
const SUITS = ['♠', '♥', '♣', '♦'];
const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
const RANK_VALUES = { '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14 };
const PHASE = { PREFLOP: 'PREFLOP', FLOP: 'FLOP', TURN: 'TURN', RIVER: 'RIVER', SHOWDOWN: 'SHOWDOWN', IDLE: 'IDLE' };
const SEATS_COUNT = 9;
const DEFAULT_STARTING_CHIPS = 1000; 
const BIG_BLIND = 20; 
const MAX_RAISES = 4;
const ACTION_TIMEOUT_SEC = 180; // [优化5]：改为3分钟

// --- 3. 辅助函数 ---

// [新增] 音频路径获取函数：解决部署路径问题
const getAudioUrl = (filename) => {
  const baseUrl = process.env.PUBLIC_URL || '';
  // 这里的 sounds 前面不需要斜杠，baseUrl 可能会带
  // 如果 baseUrl 是空字符串，则路径为 /sounds/xxx
  return baseUrl ? `${baseUrl}/sounds/${filename}` : `/sounds/${filename}`;
};

const generateDeck = () => {
  const deck = [];
  for (let s of SUITS) for (let r of RANKS) deck.push({ suit: s, rank: r });
  return deck.sort(() => Math.random() - 0.5);
};

const compareScores = (a, b) => {
  const len = Math.max(a?.length||0, b?.length||0);
  for(let i=0;i<len;i++) {
    if((a[i]||0)>(b[i]||0)) return 1;
    if((b[i]||0)>(a[i]||0)) return -1;
  }
  return 0;
};

const evaluateHand = (holeCards, communityCards) => {
  try {
    const safeHole = Array.isArray(holeCards) ? holeCards : [];
    const safeComm = Array.isArray(communityCards) ? communityCards : [];
    const allCards = [...safeHole, ...safeComm];
    if (allCards.length < 5) return { score: [-1], type: -1, description: '计算中' };
    const cards = allCards.filter(c => c && c.rank).map(c => ({ v: RANK_VALUES[c.rank], s: c.suit })).sort((a, b) => b.v - a.v);
    const counts = {}; const suits = {};
    cards.forEach(c => { counts[c.v] = (counts[c.v] || 0) + 1; suits[c.s] = (suits[c.s] || 0) + 1; });
    const getKickers = (excludeValues, count) => cards.filter(c => !excludeValues.includes(c.v)).map(c => c.v).slice(0, count);
    const getStraightHigh = (list) => {
      const uniqueVals = Array.from(new Set(list.map(c => c.v))).sort((a, b) => b - a);
      if (uniqueVals.includes(14)) uniqueVals.push(1);
      for (let i = 0; i <= uniqueVals.length - 5; i++) { if (uniqueVals[i] - uniqueVals[i + 4] === 4) return uniqueVals[i]; }
      return null;
    };
    const flushSuit = Object.keys(suits).find(s => suits[s] >= 5);
    const flushCards = flushSuit ? cards.filter(c => c.s === flushSuit) : [];
    const strHigh = getStraightHigh(cards);
    if (flushSuit) { const sfHigh = getStraightHigh(flushCards); if (sfHigh) return { score: [8, sfHigh], type: 8, description: sfHigh === 14 ? '皇家同花顺' : '同花顺' }; }
    const dist = Object.keys(counts).map(Number).sort((a, b) => b - a);
    const quads = dist.filter(v => counts[v] === 4);
    const trips = dist.filter(v => counts[v] === 3);
    const pairs = dist.filter(v => counts[v] === 2);
    if (quads.length) return { score: [7, quads[0], ...getKickers([quads[0]], 1)], type: 7, description: '四条' };
    if (trips.length > 1) return { score: [6, trips[0], trips[1]], type: 6, description: '葫芦' };
    if (trips.length && pairs.length) return { score: [6, trips[0], pairs[0]], type: 6, description: '葫芦' };
    if (flushSuit) return { score: [5, ...flushCards.map(c => c.v).slice(0, 5)], type: 5, description: '同花' };
    if (strHigh) return { score: [4, strHigh], type: 4, description: '顺子' };
    if (trips.length) return { score: [3, trips[0], ...getKickers([trips[0]], 2)], type: 3, description: '三条' };
    if (pairs.length >= 2) return { score: [2, pairs[0], pairs[1], ...getKickers([pairs[0], pairs[1]], 1)], type: 2, description: '两对' };
    if (pairs.length) return { score: [1, pairs[0], ...getKickers([pairs[0]], 3)], type: 1, description: '一对' };
    return { score: [0, ...cards.map(c => c.v).slice(0, 5)], type: 0, description: '高牌' };
  } catch (e) { console.error("Eval Error", e); return { score: [-1], type: -1, description: '异常' }; }
};

// --- 核心逻辑：边池计算 (修复版：严格过滤弃牌玩家) ---
const calculatePots = (players, communityCards) => {
    // [修复1]：这里增加严格过滤，确保 folded 的玩家绝对不会进入 eligiblePlayers
    // 即使 players 数组里有残留数据，这里也会将其剔除
    const validPlayers = players.filter(p => p !== null);
    
    // 计算所有有效下注（包括弃牌玩家的钱，因为弃牌玩家的钱也是死钱，要进池）
    const allBets = validPlayers.map(p => p.totalBet).filter(b => b > 0);
    if (allBets.length === 0) return { logs: [], ledgerUpdates: {}, winnerDetails: [] };
    
    const levels = [...new Set(allBets)].sort((a, b) => a - b);
    let prevLevel = 0;
    const ledgerUpdates = {}; 
    const logs = [];
    const winnersMap = {};
    
    // 初始化账本
    validPlayers.forEach(p => { ledgerUpdates[p.uid] = p.chips; });
    
    levels.forEach((level, index) => {
        const step = level - prevLevel; 
        if (step <= 0) return;
        
        let potAmount = 0;
        // 收集所有人的钱（包括弃牌者）进入当前 Level 的边池
        validPlayers.forEach(p => { 
            const contribution = Math.max(0, Math.min(p.totalBet, level) - prevLevel); 
            if (contribution > 0) potAmount += contribution; 
        });
        
        if (potAmount === 0) return;
        
        // [关键修复]：争夺该边池的资格，严格排除 folded 玩家
        const eligiblePlayers = validPlayers.filter(p => !p.folded && p.totalBet >= level);
        
        if (eligiblePlayers.length === 0) { 
            // 极端情况：所有人弃牌，钱留给最后赢家（通常会在 handleAction 提前处理，这里作为兜底）
            logs.push(`Level ${level} (💎${potAmount}): 无人有资格(异常)`);
        } else if (eligiblePlayers.length === 1) {
            const winner = eligiblePlayers[0];
            ledgerUpdates[winner.uid] += potAmount;
            if (index < levels.length - 1 || eligiblePlayers.length === 1) {
                 if (!winnersMap[winner.uid]) { winnersMap[winner.uid] = { name: winner.name, amount: 0, hand: '对手弃牌/退还', type: 0 }; }
                 winnersMap[winner.uid].amount += potAmount;
            }
            logs.push(`${winner.name} 收回/赢得 💎${potAmount}`);
        } else {
            const hands = eligiblePlayers.map(p => ({ player: p, eval: evaluateHand(p.hand, communityCards) })).sort((a, b) => compareScores(b.eval.score, a.eval.score));
            const bestScore = hands[0].eval.score;
            const winners = hands.filter(h => compareScores(h.eval.score, bestScore) === 0);
            
            const share = Math.floor(potAmount / winners.length);
            const remainder = potAmount % winners.length;
            const winNames = [];
            
            winners.forEach((w, idx) => {
                let winAmt = share;
                if (idx === 0) winAmt += remainder;
                ledgerUpdates[w.player.uid] += winAmt;
                
                if (!winnersMap[w.player.uid]) { winnersMap[w.player.uid] = {  name: w.player.name,  amount: 0,  hand: w.eval.description,  type: w.eval.type  }; }
                winnersMap[w.player.uid].amount += winAmt;
                // 更新为最强牌型描述
                if (w.eval.type > winnersMap[w.player.uid].type) {
                     winnersMap[w.player.uid].hand = w.eval.description;
                     winnersMap[w.player.uid].type = w.eval.type;
                }
                winNames.push(`${w.player.name}(+${winAmt})`);
            });
            logs.push(`池(💎${potAmount}): ${winNames.join('&')} 用 ${hands[0].eval.description} 赢得`);
        }
        prevLevel = level;
    });
    
    const winnerDetails = Object.values(winnersMap).sort((a, b) => b.amount - a.amount);
    return { logs, ledgerUpdates, winnerDetails };
};

// --- 组件：ZoomPanView ---
const ZoomPanView = ({ children }) => {
  const transform = useRef({ x: 0, y: 0, scale: 1 });
  const containerRef = useRef(null);
  const [showReset, setShowReset] = useState(false);
  const gesture = useRef({ startScale: 1, startPos: { x: 0, y: 0 }, startDist: 0, isPinching: false, isPanning: false, startX: 0, startY: 0 });
  const getDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  const updateDom = () => {
    if (containerRef.current) {
      const { x, y, scale } = transform.current;
      containerRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    }
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      gesture.current.isPanning = true; gesture.current.isPinching = false; gesture.current.startX = e.touches[0].clientX; gesture.current.startY = e.touches[0].clientY; gesture.current.startPos = { ...transform.current };
    } else if (e.touches.length === 2) {
      gesture.current.isPinching = true; gesture.current.isPanning = false; gesture.current.startDist = getDistance(e.touches[0], e.touches[1]); gesture.current.startScale = transform.current.scale;
    }
  };

  const handleTouchMove = (e) => {
    let changed = false;
    if (gesture.current.isPinching && e.touches.length === 2) {
      const dist = getDistance(e.touches[0], e.touches[1]);
      const newScale = Math.min(Math.max(gesture.current.startScale * (dist / gesture.current.startDist), 0.5), 3);
      transform.current.scale = newScale; changed = true;
    } else if (gesture.current.isPanning && e.touches.length === 1) {
      const dx = e.touches[0].clientX - gesture.current.startX;
      const dy = e.touches[0].clientY - gesture.current.startY;
      transform.current.x = gesture.current.startPos.x + dx; transform.current.y = gesture.current.startPos.y + dy; changed = true;
    }
    if (changed) requestAnimationFrame(updateDom);
  };

  const handleTouchEnd = () => {
    gesture.current.isPinching = false; gesture.current.isPanning = false;
    const { x, y, scale } = transform.current;
    const isDirty = x !== 0 || y !== 0 || scale !== 1;
    if (isDirty !== showReset) setShowReset(isDirty);
  };

  const handleReset = (e) => {
    e.stopPropagation(); transform.current = { x: 0, y: 0, scale: 1 }; updateDom(); setShowReset(false);
  };

  return (
    <div className="w-full h-full overflow-hidden relative bg-black/20 touch-none" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      <div ref={containerRef} style={{ transform: `translate3d(0px, 0px, 0) scale(1)`, transformOrigin: 'center center' }} className="w-full h-full flex items-center justify-center transition-transform duration-0 ease-linear will-change-transform">
        {children}
      </div>
      {showReset && <button onClick={handleReset} className="absolute bottom-4 right-4 bg-gray-800/80 p-2 rounded-full text-white z-50 shadow-lg border border-gray-600"><RefreshCw size={16}/></button>}
    </div>
  );
};

// --- 样式组件：发牌动画更新 ---
const GlobalStyles = () => (
  <style>{`
    @keyframes dealCardAnimation {
      0% { opacity: 0; transform: translate(0, 200px) scale(0) rotate(180deg); }
      20% { opacity: 1; }
      100% { opacity: 1; transform: translate(0, 0) scale(1) rotate(0deg); }
    }
    .card-deal-anim {
      opacity: 0;
      animation: dealCardAnimation 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    }
    input[type=range] { -webkit-appearance: none; width: 100%; background: transparent; }
    input[type=range]::-webkit-slider-thumb {
      -webkit-appearance: none; height: 16px; width: 16px; border-radius: 50%;
      background: #EAB308; margin-top: -6px; box-shadow: 0 0 5px rgba(0,0,0,0.5);
    }
    input[type=range]::-webkit-slider-runnable-track { width: 100%; height: 4px; cursor: pointer; background: #4B5563; border-radius: 2px; }
  `}</style>
);

// --- 4. 主程序 (已集成 Agora + 修复 AI 逻辑) ---
export default function App() {
  const [user, setUser] = useState(null);
  const [currentRoomId, setCurrentRoomId] = useState(null); 
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inputName, setInputName] = useState('');
  const [activeTab, setActiveTab] = useState('table');
  const [showRaiseControl, setShowRaiseControl] = useState(false);
  const [raiseAmount, setRaiseAmount] = useState(0);
  const [prevCommunityCards, setPrevCommunityCards] = useState([]);
  const [recentAction, setRecentAction] = useState(null);
  const [volumes, setVolumes] = useState({ bgm: 0.3, sfx: 0.6, mic: 1.0, speaker: 1.0 });
  const [showSoundMenu, setShowSoundMenu] = useState(false);

  // --- Agora 状态 ---
  // 注意：你需要替换下面的 AGORA_APP_ID 为你自己的
  const AGORA_APP_ID = "35496be2aa254e50933622230102d1a8";
  const [agoraClient] = useState(() => AgoraRTC.createClient({ mode: "rtc", codec: "vp8" }));
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [isMicOn, setIsMicOn] = useState(false);

  const timerRef = useRef(null);
  const adjustRef = useRef(null);
  const bgmRef = useRef(null);

  // --- Agora: 监听房间变化并加入/退出语音频道 ---
  useEffect(() => {
    // 如果没有用户或不在房间，清理 Agora 资源
    if (!user || !currentRoomId) {
        if (localAudioTrack) {
            localAudioTrack.close();
            setLocalAudioTrack(null);
            setIsMicOn(false);
        }
        if (agoraClient && agoraClient.connectionState !== 'DISCONNECTED') {
            agoraClient.leave().catch(err => console.warn("Agora leave error", err));
        }
        return;
    }

    const initAgora = async () => {
        try {
            // 防止重复加入
            if (agoraClient.connectionState === 'CONNECTED' || agoraClient.connectionState === 'CONNECTING') {
                return;
            }

            // 监听远程用户发布音频
            agoraClient.on("user-published", async (user, mediaType) => {
                await agoraClient.subscribe(user, mediaType);
                if (mediaType === "audio") {
                    user.audioTrack.play();
                    // 同步当前设置的音量 (Agora 使用 0-100)
                    user.audioTrack.setVolume(volumes.speaker * 100); 
                }
            });

            agoraClient.on("user-unpublished", (user) => {
                // SDK 会自动处理
            });

            // 加入频道 (使用房间号作为频道名，用户ID作为UID)
            await agoraClient.join(AGORA_APP_ID, currentRoomId, null, user.id);
            console.log("Agora Joined Room:", currentRoomId);
        } catch (error) {
            console.error("Agora Error:", error);
        }
    };

    initAgora();

    // 组件卸载或房间变化时的清理
    return () => {
        agoraClient.removeAllListeners();
        if(localAudioTrack) {
            localAudioTrack.close();
        }
        // 这里的 leave 可能会在重连时被触发，上面已有处理
    };
  }, [currentRoomId, user]); 

  // --- Agora: 麦克风开关逻辑 ---
  const toggleMic = async () => {
      try {
          if (isMicOn) {
              // 关闭麦克风
              if (localAudioTrack) {
                  await agoraClient.unpublish(localAudioTrack);
                  localAudioTrack.close();
                  setLocalAudioTrack(null);
              }
              setIsMicOn(false);
          } else {
              // 打开麦克风
              // 注意：Agora Web SDK 采集音量由系统控制，这里主要负责创建轨道
              const track = await AgoraRTC.createMicrophoneAudioTrack();
              await agoraClient.publish(track);
              setLocalAudioTrack(track);
              setIsMicOn(true);
          }
      } catch (e) {
          console.error("Mic Error:", e);
          alert("无法访问麦克风，请检查浏览器权限");
          setIsMicOn(false);
      }
  };

  // --- Agora: 监听输出音量变化 ---
  useEffect(() => {
      if (agoraClient) {
          // 调整所有远程用户的音量
          agoraClient.remoteUsers.forEach(user => {
              if (user.audioTrack) {
                  user.audioTrack.setVolume(volumes.speaker * 100);
              }
          });
      }
  }, [volumes.speaker]);

  // --- 音效播放 (使用 getAudioUrl 修复路径) ---
  const playSound = (type) => {
    try {
      const url = getAudioUrl(`${type}.mp3`);
      const audio = new Audio(url); 
      audio.volume = volumes.sfx; 
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          // 忽略自动播放限制错误
        });
      }
    } catch (e) { console.error("Audio error:", e); }
  };

  // --- BGM 控制 ---
  useEffect(() => {
    if (currentRoomId && !bgmRef.current) {
        const url = getAudioUrl('bgm.mp3');
        const audio = new Audio(url);
        audio.loop = true;
        audio.volume = volumes.bgm; 
        bgmRef.current = audio;
        audio.play().catch((err) => {
            console.log("BGM autoplay blocked", err);
            const unlock = () => { audio.play(); document.removeEventListener('click', unlock); };
            document.addEventListener('click', unlock);
        });
    } else if (!currentRoomId && bgmRef.current) {
        bgmRef.current.pause();
        bgmRef.current = null;
    }
    return () => { if(bgmRef.current) { bgmRef.current.pause(); bgmRef.current = null; } };
  }, [currentRoomId]);

  // 实时更新 BGM 音量
  useEffect(() => { if(bgmRef.current) { bgmRef.current.volume = volumes.bgm; } }, [volumes.bgm]);

  // --- // --- 监听动作并播放音效 [修复版] ---
  useEffect(() => {
    if (!recentAction) return;
    if (Date.now() - recentAction.ts > 2000) return; // 放宽一点时间窗口
    
    // [修复3]：使用 includes 模糊匹配，防止 "加注至" 这种变种导致无声
    // 同时因为 handleAction 已经修复为纯文本，这里的匹配会非常精准
    const type = recentAction.type;
    
    if (type.includes('弃牌')) playSound('fold');
    else if (type.includes('过牌')) playSound('check');
    else if (type.includes('跟注')) playSound('chip');
    else if (type.includes('加注')) playSound('chip');
    else if (type.includes('All-In')) playSound('chip');
    
  }, [recentAction]);

  // --- 发牌音效与动画 [修复版：解决All-In后无声] ---
  const prevPhaseRef = useRef(PHASE.IDLE);
  const prevCommCardsLenRef = useRef(0);

  useEffect(() => {
    if (!gameState) return;
    if (gameState.winner) playSound('win');
    
    // 如果是 IDLE 阶段，重置计数器，确保下一局发牌从 0 开始算
    if (gameState.phase === PHASE.IDLE) {
        prevCommCardsLenRef.current = 0;
    }
    
    const currentCommLen = gameState.communityCards.length;
    const prevCommLen = prevCommCardsLenRef.current;
    
    // [关键修复]：All-in 跑牌时，communityCards 会增加。
    // 即使更新很快，只要 gameState 变化，这里就会执行。
    // 我们移除对 phase 的强依赖，只看牌数量变化。
    if (currentCommLen > prevCommLen) {
       const newCount = currentCommLen - prevCommLen;
       // 检测是否是一次性发多张（如 Flop）还是逐张（All-in Runout）
       // 如果是 Preflop -> Flop，prev=0, current=3.
       // 如果是 Turn -> River (Runout), prev=3, current=4...
       
       for(let i=0; i<newCount; i++) {
           // 计算绝对索引
           const absoluteIndex = prevCommLen + i;
           // 基础延迟：如果是 IDLE (复盘) 不需要延迟
           // 如果是游戏中，延迟根据牌的位置决定。
           // 第一张牌(0) 立即发，第二张(1) 300ms... 第四张(3) 900ms
           const delay = gameState.phase === PHASE.IDLE ? 0 : absoluteIndex * 250; 
           
           setTimeout(() => playSound('deal'), delay);
       }
    }
    
    prevCommCardsLenRef.current = currentCommLen;
    
    // 手牌发牌音效 (PREFLOP)
    if (prevPhaseRef.current === PHASE.IDLE && gameState.phase === PHASE.PREFLOP) {
       const activeCount = gameState.players.filter(p => p).length;
       // 第一圈
       for(let i=0; i<activeCount; i++) setTimeout(() => playSound('deal'), i * 150);
       // 第二圈
       for(let i=0; i<activeCount; i++) setTimeout(() => playSound('deal'), (activeCount * 150) + (i * 150));
    }
    
    prevPhaseRef.current = gameState.phase;
  }, [gameState]); // 依赖项保持 gameState，确保每次 update 都触发

  // --- LeanCloud 初始化逻辑 ---
  useEffect(() => {
    const init = async () => {
      try {
        let curr = AV.User.current() || await AV.User.loginAnonymously();
        setUser(curr);
        setLoading(false);
      } catch (e) { setTimeout(() => window.location.reload(), 2000); }
    };
    init();
  }, []);

  useEffect(() => {
    if (!user || !currentRoomId) { setGameState(null); return; }
    setLoading(true);
    const query = new AV.Query('PokerRooms');
    query.equalTo('roomId', currentRoomId);
    let sub;
    const setup = async () => {
      try {
        sub = await query.subscribe();
        
        // [新增]：监听房间被删除（房主关闭房间）事件
        sub.on('delete', () => {
            alert("房主已解散房间");
            setGameState(null);
            setCurrentRoomId(null);
        });
        // [新增结束]

        sub.on('update', obj => {
            const data = obj.attributes;
            setGameState(data);
            if (data.phase === PHASE.SHOWDOWN && data.communityCards.length > 0) setPrevCommunityCards(data.communityCards);
            if (data.phase === PHASE.PREFLOP) setPrevCommunityCards([]);
            if (data.lastAction && data.lastAction.ts !== recentAction?.ts) {
                setRecentAction(data.lastAction);
                setTimeout(() => setRecentAction(null), 3000);
            }
        });
        const first = await query.first();
        if (first) setGameState(first.attributes);
        else { alert("房间不存在"); setCurrentRoomId(null); }
      } catch(err) { console.error(err); }
      setLoading(false);
    };
    setup();
    return () => sub?.unsubscribe();
  }, [user, currentRoomId]);

  const runTx = async (fn) => { 
      try { 
          if(!currentRoomId) return; 
          const query = new AV.Query('PokerRooms');
          query.equalTo('roomId', currentRoomId);
          const room = await query.first();
          if(!room) return;
          const t = { update: (ref, data) => room.save(data), get: async () => ({ data: () => room.attributes, exists: () => true }) };
          await fn(t, room.attributes, null); 
      } catch(e){ 
          console.error(e);
          if (typeof e === 'string') alert(e); 
      } 
  };

  const simpleUpdate = async (field, val, log) => {
    if(!currentRoomId) return;
    const query = new AV.Query('PokerRooms');
    query.equalTo('roomId', currentRoomId);
    const room = await query.first();
    if(room) {
        const updateData = { [field]: val };
        if(log) {
            const logs = room.attributes.logs || [];
            updateData.logs = [...logs, log];
        }
        await room.save(updateData);
    }
  };

  // --- 核心动作逻辑 (HandleAction) ---
  const handleAction = async (action, amount = 0, actingSeat = -1) => {
    if (!currentRoomId || !gameState) return;
    await runTx(async (t, d) => {
        // [Opt 4 & 新增功能] 特殊动作：步进式发牌 (由 useEffect 触发)
        if (action === 'deal_next_street') {
             const { deck, communityCards, phase } = d;
             const newComm = [...communityCards];
             const newDeck = [...deck];
             let nextPhase = phase;
             
             // 简单的发牌状态机
             if (phase === PHASE.PREFLOP) {
                 if(newDeck.length) newDeck.pop(); // 销牌
                 for(let i=0;i<3;i++) if(newDeck.length) newComm.push(newDeck.pop());
                 nextPhase = PHASE.FLOP;
             } else if (phase === PHASE.FLOP) {
                 if(newDeck.length) newDeck.pop();
                 if(newDeck.length) newComm.push(newDeck.pop());
                 nextPhase = PHASE.TURN;
             } else if (phase === PHASE.TURN) {
                 if(newDeck.length) newDeck.pop();
                 if(newDeck.length) newComm.push(newDeck.pop());
                 nextPhase = PHASE.RIVER;
             } else if (phase === PHASE.RIVER) {
                 nextPhase = PHASE.SHOWDOWN;
             }

             // 到达结算阶段 (或者是 Rabbit Hunting 结束)
             if (nextPhase === PHASE.SHOWDOWN) {
                  const activeSurvivors = d.players.filter(p => p && !p.folded);
                  
                  // [新增逻辑] 分支A: 仅剩1人 (弃牌获胜的 Rabbit Hunting 结束)
                  if (activeSurvivors.length === 1) {
                      const winner = activeSurvivors[0];
                      const pot = d.pot;
                      
                      winner.chips += pot;
                      winner.showCards = false; // 强制不亮牌
                      
                      const ledger = d.ledger || {};
                      if (ledger[winner.uid]) ledger[winner.uid].chips = winner.chips;

                      // 更新所有玩家状态
                      const ps = d.players.map(p => {
                          if (p && p.uid === winner.uid) return winner;
                          return p;
                      });

                      t.update(null, {
                          players: ps, pot: 0, phase: PHASE.SHOWDOWN,
                          communityCards: newComm, deck: newDeck,
                          winner: { name: winner.name, amount: pot },
                          // 构造赢家详情，但保持手牌描述模糊
                          winnerDetails: [{ name: winner.name, amount: pot, hand: "对手弃牌", type: 0 }],
                          winningHand: { description: "对手弃牌获胜", type: 0 },
                          logs: [...(d.logs||[]), `发牌结束，🏆 ${winner.name} 赢走底池 💎${pot}`],
                          actionsThisRound: 0, raisesThisRound: 0, lastRaiseDiff: 0
                      });
                  } 
                  // [原有逻辑] 分支B: 多人 All-in 结算
                  else {
                      const result = calculatePots(d.players, newComm);
                      const ps = d.players.map(p => {
                          if (p && result.ledgerUpdates[p.uid] !== undefined) {
                              p.chips = result.ledgerUpdates[p.uid];
                              if (d.ledger && d.ledger[p.uid]) d.ledger[p.uid].chips = p.chips;
                          }
                          return p;
                      });
                      t.update(null, {
                          players: ps, pot: 0, phase: PHASE.SHOWDOWN,
                          communityCards: newComm, deck: newDeck,
                          winner: { name: "All-in 结算" },
                          winnerDetails: result.winnerDetails,
                          winningHand: result.mainHand || { description: "见战报" },
                          logs: [...(d.logs||[]), ...result.logs],
                          actionsThisRound: 0, raisesThisRound: 0, lastRaiseDiff: 0
                      });
                  }
             } else {
                  // 仅发牌，不结算，继续跑下一轮
                  t.update(null, {
                      phase: nextPhase,
                      communityCards: newComm,
                      deck: newDeck,
                      actionsThisRound: 0 
                  });
             }
             return;
        }

        // --- 常规玩家操作逻辑 ---
        let sIdx = actingSeat === -1 ? d.players.findIndex(p => p && p.uid === user.id) : actingSeat;
        if (sIdx === -1 || d.currentTurnIndex !== sIdx) return; 

        const ps = [...d.players];
        const p = ps[sIdx];
        if (!p) return;

        let { pot, phase, deck, communityCards, actionsThisRound } = d;
        let minBet = d.minBet; 
        let raisesThisRound = d.raisesThisRound || 0;
        let lastRaiseDiff = d.lastRaiseDiff || (minBet || 20); 
        let logs = [...(d.logs || [])];
        let actionType = "";
        let actionAmount = 0;

        // [修复2 & 3]：分离动作类型与数值，解决音效匹配失败和UI显示重叠问题
        if (action === 'fold') {
            p.folded = true; 
            p.hasActed = true; 
            p.showCards = false; 
            actionType = "弃牌";
        } else if (action === 'check') {
             if (p.bet < minBet) { 
                 const diff = minBet - p.bet;
                 if (p.chips > diff) {
                     p.chips -= diff; p.bet += diff; p.totalBet += diff; pot += diff;
                     actionType = "跟注"; // [修复] 纯文本
                     actionAmount = diff; // [修复] 数值分离
                 } else {
                     p.chips = 0; p.bet += p.chips; p.totalBet += p.chips; pot += p.chips;
                     p.allIn = true; 
                     actionType = "All-In";
                     actionAmount = p.totalBet;
                 }
             } else { actionType = "过牌"; }
             p.hasActed = true;
        } else if (action === 'call') {
             const diff = minBet - p.bet;
             if (p.chips > diff) {
                 p.chips -= diff; p.bet += diff; p.totalBet += diff; pot += diff;
                 actionType = "跟注"; 
                 actionAmount = diff;
             } else {
                 const real = p.chips;
                 p.chips = 0; p.bet += real; p.totalBet += real; pot += real;
                 p.allIn = true; 
                 actionType = "All-In"; 
                 actionAmount = real;
             }
             p.hasActed = true;
        } else if (action === 'raise') {
             // ... (保留原有的 minRaiseTotal 计算逻辑) ...
             const currentMinRaiseDiff = d.lastRaiseDiff || (minBet || 20);
             const minLegalRaiseTotal = (minBet || 0) + currentMinRaiseDiff;

             if (amount <= minBet && amount < p.chips + p.bet) {
                 // 修正逻辑：如果加注额不够，算作跟注
                 const diff = minBet - p.bet;
                 if (p.chips > diff) {
                     p.chips -= diff; p.bet += diff; p.totalBet += diff; pot += diff;
                     actionType = "跟注"; // [修复]
                     actionAmount = diff;
                 } else {
                     const real = p.chips;
                     p.chips = 0; p.bet += real; p.totalBet += real; pot += real;
                     p.allIn = true; 
                     actionType = "All-In";
                     actionAmount = real;
                 }
                 p.hasActed = true; 
             } else {
                 const diff = amount - p.bet;
                 if (diff >= p.chips) {
                     const real = p.chips;
                     p.chips = 0; p.bet += real; p.totalBet += real; pot += real;
                     p.allIn = true;
                     
                     // All-in 逻辑判断
                     if (p.bet > minBet) {
                          const actualRaiseDiff = p.bet - minBet;
                          if (actualRaiseDiff >= lastRaiseDiff) {
                              raisesThisRound++; actionsThisRound = 1; lastRaiseDiff = actualRaiseDiff; minBet = p.bet;
                              ps.forEach(pl => { if(pl) pl.hasActed = false; });
                              p.hasActed = true; 
                              actionType = "All-In 加注"; // [修复]
                          } else {
                              minBet = p.bet;
                              actionType = "All-In (短码)"; 
                              p.hasActed = true;
                          }
                     } else {
                          actionType = "All-In"; 
                          p.hasActed = true;
                     }
                     actionAmount = real;
                 } else {
                     let finalAmount = amount;
                     if (finalAmount < minLegalRaiseTotal) finalAmount = minLegalRaiseTotal;
                     const finalDiff = finalAmount - p.bet;
                     p.chips -= finalDiff; p.bet = finalAmount; p.totalBet += finalDiff; pot += finalDiff; 
                     lastRaiseDiff = finalAmount - minBet;
                     minBet = finalAmount;
                     raisesThisRound++; actionsThisRound = 1; 
                     ps.forEach(pl => { if(pl) pl.hasActed = false; });
                     p.hasActed = true;
                     
                     actionType = "加注"; // [修复]：纯文本
                     actionAmount = finalAmount;
                 }
             }
        }

        const lastAction = { uid: p.uid, name: p.name, type: actionType, amount: actionAmount, ts: Date.now() };
        p.currentAction = lastAction;
        logs.push(`${p.name} ${actionType}`);

        const ledger = d.ledger || {};
        if (ledger[p.uid]) ledger[p.uid].chips = p.chips;

        const activePlayers = ps.filter(pl => pl && !pl.folded);
        
        // --- [新增功能] 只有一人存活 (对手弃牌) ---
        if (activePlayers.length === 1) {
             const winner = activePlayers[0];
             
             // [新逻辑]: 如果还有公共牌没发完，进入 "跑牌模式" (Rabbit Hunting)
             // 1. 如果公共牌不足5张，锁定 currentTurnIndex 为 -1
             // 2. useEffect 会检测到 (TurnIndex=-1 && Phase!=Showdown && !Winner)，从而每秒触发 deal_next_street
             if (communityCards.length < 5) {
                 t.update(null, {
                     players: ps, pot,
                     currentTurnIndex: -1, // 锁定操作，触发自动发牌
                     logs: [...logs, "胜负已分，展示剩余公共牌..."],
                     lastAction, ledger
                 });
                 return;
             }

             // [原有逻辑]: 如果牌已经发完了(比如河牌弃牌)，直接结算
             winner.chips += pot;
             if (ledger[winner.uid]) ledger[winner.uid].chips = winner.chips;
             winner.showCards = false; // 确保不亮牌

             t.update(null, { 
                 players: ps, pot: 0, phase: PHASE.SHOWDOWN, 
                 winner: { name: winner.name, amount: pot }, 
                 winnerDetails: [{ name: winner.name, amount: pot, hand: "对手弃牌", type: 0 }],
                 winningHand: { description: "对手弃牌获胜", type: 0 }, 
                 logs: [...logs, `🏆 ${winner.name} 赢走底池 💎${pot}`], 
                 lastAction, ledger,
                 actionsThisRound: 0, raisesThisRound: 0, lastRaiseDiff: 0
             });
             return;
        }

        // 3. 寻找下一个行动者
        let nextTurn = (sIdx + 1) % SEATS_COUNT;
        const findNextActive = (startIdx) => {
             let n = startIdx; let count = 0;
             while(count < SEATS_COUNT) {
                 const pl = ps[n];
                 if (pl && !pl.folded && !pl.allIn) return n;
                 n = (n + 1) % SEATS_COUNT; count++;
             }
             return -1;
        };

        const activeNonAllIn = activePlayers.filter(pl => !pl.allIn);
        const isBetsEqual = activeNonAllIn.every(pl => pl.bet === minBet);
        const everyoneActed = activeNonAllIn.every(pl => pl.hasActed);
        
        let isRoundOver = false;
        if (activeNonAllIn.length === 0) isRoundOver = true;
        else if (isBetsEqual && everyoneActed) isRoundOver = true;

        if (isRoundOver) {
             ps.forEach(pl => { 
                 if(pl) { pl.bet = 0; pl.currentAction = null; pl.hasActed = false; } 
             });
             actionsThisRound = 0; raisesThisRound = 0; lastRaiseDiff = 0;

             // --- [优化4] All-In 阶段步进式逻辑 ---
             if (activeNonAllIn.length < 2 && activePlayers.length >= 2) {
                 logs.push("All-In 对决！");
                 
                 // 锁定操作，触发 useEffect 自动跑牌
                 t.update(null, {
                    players: ps, pot,
                    currentTurnIndex: -1, 
                    minBet: 0, actionsThisRound, raisesThisRound, lastRaiseDiff,
                    logs, lastAction, ledger
                 });
                 return;

             } else {
                 // 正常流程：进入下一阶段
                 const phaseMap = { [PHASE.PREFLOP]: PHASE.FLOP, [PHASE.FLOP]: PHASE.TURN, [PHASE.TURN]: PHASE.RIVER, [PHASE.RIVER]: PHASE.SHOWDOWN };
                 const nextPhase = phaseMap[phase] || PHASE.SHOWDOWN;
                 
                 if (nextPhase === PHASE.SHOWDOWN) {
                      const result = calculatePots(ps, communityCards);
                      ps.forEach(pl => {
                          if (pl && result.ledgerUpdates[pl.uid] !== undefined) {
                              pl.chips = result.ledgerUpdates[pl.uid];
                              if (ledger[pl.uid]) ledger[pl.uid].chips = pl.chips;
                          }
                      });
                      t.update(null, {
                          players: ps, pot: 0, phase: PHASE.SHOWDOWN,
                          winner: { name: "结算完成" },
                          winnerDetails: result.winnerDetails,
                          winningHand: result.mainHand || { description: "见战报" },
                          logs: [...logs, ...result.logs], lastAction, ledger,
                          actionsThisRound: 0, raisesThisRound: 0, lastRaiseDiff: 0
                      });
                      return;
                 } else {
                      if (deck.length > 0) {
                          if(deck.length) deck.pop(); 
                          if (nextPhase === PHASE.FLOP) { for(let k=0;k<3;k++) if(deck.length) communityCards.push(deck.pop()); } 
                          else { if(deck.length) communityCards.push(deck.pop()); }
                      }
                      let nextActor = findNextActive((d.dealerIndex + 1) % SEATS_COUNT);
                      
                      t.update(null, {
                          players: ps, pot, phase: nextPhase, deck, communityCards,
                          currentTurnIndex: nextActor, minBet: 0, 
                          actionsThisRound: 0, raisesThisRound: 0, lastRaiseDiff: 0,
                          logs, lastAction, ledger
                      });
                      return;
                 }
             }
        } else {
             let nextActor = findNextActive(nextTurn);
             t.update(null, { players: ps, pot, currentTurnIndex: nextActor, minBet, actionsThisRound, raisesThisRound, lastRaiseDiff, logs, lastAction, ledger });
        }
    });
};

  // --- AI 辅助函数 (评分/听牌/顶对) ---
  const ratePreflopHand = (card1, card2) => {
    if (!card1 || !card2) return 0;
    let v1 = RANK_VALUES[card1.rank];
    let v2 = RANK_VALUES[card2.rank];
    if (v2 > v1) [v1, v2] = [v2, v1]; 
    let score = 0;
    if (v1 === 14) score += 10; 
    else if (v1 === 13) score += 8; 
    else if (v1 === 12) score += 7; 
    else if (v1 === 11) score += 6; 
    else score += v1 / 2;
    if (v1 === v2) { score *= 2.2; if (score < 12) score = 12; }
    if (card1.suit === card2.suit) score += 3.5; 
    const gap = v1 - v2;
    if (gap === 1) score += 3; 
    else if (gap === 2) score += 1.5; 
    if (gap > 4 && v1 < 10) score -= 2;
    return Math.max(0, score);
  };

  const hasFlushDraw = (holeCards, commCards) => {
    if(!commCards || commCards.length < 3) return false;
    const all = [...holeCards, ...commCards];
    const suits = {};
    all.forEach(c => suits[c.suit] = (suits[c.suit]||0) + 1);
    return Object.values(suits).some(count => count === 4);
  };

  const isTopPairOrBetter = (holeCards, commCards) => {
    if(!commCards.length) return false;
    const boardMax = Math.max(...commCards.map(c => RANK_VALUES[c.rank]));
    const holeMax = Math.max(RANK_VALUES[holeCards[0].rank], RANK_VALUES[holeCards[1].rank]);
    const holeMin = Math.min(RANK_VALUES[holeCards[0].rank], RANK_VALUES[holeCards[1].rank]);
    if(holeMin > boardMax && holeMax === holeMin) return true;
    if(holeMax === boardMax && commCards.some(c => RANK_VALUES[c.rank] === holeMax)) return true;
    return false;
  };

  // --- [AI 逻辑修复]：增加手牌判空保护与加注金额修正 ---
const handleBotTurn = (idx, bot) => {
    // 1. 安全检查：如果机器人数据异常或没手牌，直接过牌/弃牌，防止崩溃
    if (!bot || !gameState || !bot.hand || bot.hand.length < 2) {
        console.warn("Bot hand missing, skipping turn.");
        handleAction('check', 0, idx);
        return;
    }

    const phase = gameState.phase;
    const minBet = gameState.minBet || 20;
    const toCall = minBet - bot.bet;
    const pot = gameState.pot;
    // 防止除以0错误
    const potOdds = (pot + toCall) > 0 ? toCall / (pot + toCall) : 0;
    
    let action = 'fold';
    let amount = 0;
    const rand = Math.random();

    if (phase === PHASE.PREFLOP) {
        // 此时 hand[0] 肯定是存在的
        const score = ratePreflopHand(bot.hand[0], bot.hand[1]);
        if (score >= 18) { action = 'raise'; amount = minBet * (3 + Math.floor(rand * 2)); }
        else if (score >= 12) {
            if (toCall > minBet * 4) action = rand > 0.2 ? 'call' : 'fold';
            else { action = rand > 0.4 ? 'raise' : 'call'; amount = minBet * 2.5; }
        }
        else if (score >= 8) { if (toCall <= minBet * 3) action = 'call'; else action = rand > 0.7 ? 'call' : 'fold'; }
        else if (score >= 5) { if (toCall <= minBet) action = 'call'; else if (potOdds < 0.15) action = 'call'; else action = 'fold'; }
        else { 
            if (toCall === 0) action = 'check'; 
            else if (rand > 0.95 && activePlayersCount(gameState) < 4) { action = 'raise'; amount = minBet * 2; } 
            else action = 'fold'; 
        }
    }
    else {
        const evalResult = evaluateHand(bot.hand, gameState.communityCards);
        const handType = evalResult.type;
        const isDraw = hasFlushDraw(bot.hand, gameState.communityCards);
        const isTop = isTopPairOrBetter(bot.hand, gameState.communityCards);

        if (handType >= 3) { if (rand > 0.7) { action = 'check'; } else { action = 'raise'; amount = bot.bet + Math.floor(pot * (0.4 + rand * 0.4)); } }
        else if (handType === 2) { if (toCall === 0) { action = 'raise'; amount = bot.bet + Math.floor(pot * 0.5); } else { action = 'call'; } }
        else if (handType === 1) {
            if (isTop) { if (toCall < pot * 0.8) action = 'call'; if (rand > 0.6) { action = 'raise'; amount = bot.bet + Math.floor(pot * 0.4); } else action = 'call'; }
            else { if (toCall < pot * 0.4) action = 'call'; else if (toCall === 0) action = 'check'; else action = rand > 0.6 ? 'fold' : 'call'; }
        }
        else if (isDraw) { if (rand > 0.5) { action = 'raise'; amount = bot.bet + Math.floor(pot * 0.6); } else action = 'call'; }
        else {
            if (toCall === 0) { if (rand > 0.7) { action = 'raise'; amount = bot.bet + Math.floor(minBet * 2); } else action = 'check'; }
            else { if (potOdds < 0.1 && rand > 0.5) action = 'call'; else if (rand > 0.90) { action = 'raise'; amount = bot.bet + pot; } else action = 'fold'; }
        }
    }

    // 2. 加注金额修正：确保不低于最小加注额
    if (action === 'raise') {
        const lastDiff = gameState.lastRaiseDiff || 20;
        const minLegalRaise = (gameState.minBet || 20) + lastDiff;
        if (amount < minLegalRaise) amount = minLegalRaise;
        
        // 确保不超过自身筹码
        const maxChips = bot.chips + bot.bet;
        if (amount >= maxChips) amount = maxChips;
    }

    if (action === 'call' && toCall >= bot.chips) action = 'call'; // 实际上是 All-in，后端逻辑会处理
    
    // 执行操作
    handleAction(action, amount, idx);
};
  const activePlayersCount = (gs) => gs.players.filter(p => p && !p.folded).length;

  // --- 房间管理逻辑 ---
  const createRoom = async () => {
    if (!user) return alert("正在连接服务器...");
    const newRoomId = Math.floor(100000 + Math.random() * 900000).toString();
    const PokerRoom = AV.Object.extend('PokerRooms');
    const room = new PokerRoom();
    await room.save({
        roomId: newRoomId,
        players: Array(SEATS_COUNT).fill(null), communityCards: [], deck: [], pot: 0,
        currentTurnIndex: -1, dealerIndex: 0, phase: PHASE.IDLE, 
        bank: { loans: [], history: [] }, ledger: {},
        // [新增] handHistory 用于后台存储每局详细数据
        handHistory: [], 
        logs: ["学习室已建立"], winner: null, winningHand: null,
        
        // [修复核心]：初始化两个字段
        minBet: BIG_BLIND,      // 当前局的最小下注（会随游戏变动）
        blindConfig: BIG_BLIND, // 房间锁定的盲注配置（只随设置变动，不随游戏变动）

        ownerId: user.id, actionsThisRound: 0, raisesThisRound: 0, lastRaiseDiff: 0,
        startingChips: DEFAULT_STARTING_CHIPS, 
        initialChipsSet: false, 
        messages: [], lastAction: null
    });
    setCurrentRoomId(newRoomId);
  };

  const joinRoom = async (idToJoin) => {
      if (!idToJoin || idToJoin.length !== 6) return alert("请输入6位房间号");
      const query = new AV.Query('PokerRooms');
      query.equalTo('roomId', idToJoin);
      const room = await query.first();
      if (room) setCurrentRoomId(idToJoin);
      else alert("学习室不存在");
  };

  const closeRoom = async () => {
      if (!currentRoomId || !window.confirm("关闭房间？")) return;
      const query = new AV.Query('PokerRooms');
      query.equalTo('roomId', currentRoomId);
      const room = await query.first();
      if(room) await room.destroy();
      setCurrentRoomId(null);
  };

  const kickBot = async (seatIndex) => {
    runTx((t, d) => {
        const newPlayers = [...d.players];
        newPlayers[seatIndex] = null;
        t.update(null, { players: newPlayers, logs: [...(d.logs||[]), `🤖 Bot 离座`] });
    });
  };

  // --- [开始替换] 修改后的 useEffect ---
  // --- All-In 自动步进发牌逻辑 [优化4] ---
  useEffect(() => {
    if (!gameState || !user || !currentRoomId || gameState.ownerId !== user.id) return;
    
    // 判断是否处于 All-In 状态（即：还没到 Showdown，但没有人可以行动了，或者只剩1人没All-In但也结束了行动）
    // 注意：我们在 handleAction 里把 All-In 时的 currentTurnIndex 设为了 -1
    const isAllInScenario = (gameState.currentTurnIndex === -1 && gameState.phase !== PHASE.SHOWDOWN && gameState.phase !== PHASE.IDLE && !gameState.winner);

    if (isAllInScenario) {
        // 1秒后触发下一轮发牌
        const t = setTimeout(() => {
            handleAction('deal_next_street');
        }, 1000);
        return () => clearTimeout(t);
    }
  }, [gameState, user, currentRoomId]);

// --- [修复补回]：监听轮次，触发 Bot 自动打牌 与 玩家超时自动弃牌 ---
  useEffect(() => {
    // 1. 基础检查：只有房主端负责计算 Bot 逻辑，防止多人在线时指令冲突
    if (!gameState || !user || !currentRoomId || gameState.ownerId !== user.id) return;
    
    const currIdx = gameState.currentTurnIndex;
    
    // 如果当前轮次有效
    if (currIdx !== -1) {
       const p = gameState.players[currIdx];
       
       // A. 机器人逻辑：如果是机器人且游戏正在进行中
       if (p && p.isBot && gameState.phase !== PHASE.IDLE && gameState.phase !== PHASE.SHOWDOWN) {
          // 延迟 1.5 秒模拟思考，增加真实感
          const t = setTimeout(() => {
              // 双重检查：确保 timer 触发时轮次没有发生变化
              if (gameState.currentTurnIndex === currIdx) {
                  handleBotTurn(currIdx, p);
              }
          }, 1500);
          return () => clearTimeout(t);
       }

       // B. 超时判定逻辑：如果不是机器人，检查是否超时 (180s)
       // 防止玩家掉线导致游戏卡死
       if (p && !p.isBot && gameState.lastAction) {
           const timePassed = (Date.now() - gameState.lastAction.ts) / 1000;
           // 这里加 5 秒缓冲，避免客户端倒计时刚到0就立刻弃牌，体验不好
           if (timePassed > ACTION_TIMEOUT_SEC + 5) {
               console.log(`玩家 ${p.name} 超时，自动弃牌`);
               handleAction('fold', 0, currIdx);
           }
       }
    }
  }, [gameState, user, currentRoomId]); 
  // 依赖项包含 gameState，确保每次有人行动后，state更新，这个Effect都会重新运行

  // --- [新增修复2]：Bot 在复盘阶段自动亮牌 ---
  useEffect(() => {
    // 只有房主有权限操作 Bot，且必须在 IDLE 阶段
    if (!gameState || !user || !currentRoomId || gameState.ownerId !== user.id || gameState.phase !== PHASE.IDLE) return;

    // 筛选出：是机器人 && 有手牌 && 还没亮牌 的玩家
    const botsHidden = gameState.players.filter(p => p && p.isBot && p.hand.length > 0 && !p.showCards);

    if (botsHidden.length > 0) {
        // 延迟 1 秒执行，让人类玩家先看清结算框，再看 Bot 亮牌
        const t = setTimeout(() => {
            runTx((t, d) => {
                const ps = d.players.map(p => {
                    if (p && p.isBot && p.hand.length > 0 && !p.showCards) {
                        return { ...p, showCards: true };
                    }
                    return p;
                });
                // 记录一条日志让动作更自然
                t.update(null, { players: ps, logs: [...(d.logs || []), "🤖 Bot 自动亮出了底牌"] });
            });
        }, 1000);
        return () => clearTimeout(t);
    }
  }, [gameState, user, currentRoomId]);

  // --- 自动重置房间 (倒计时结束自动进入等待阶段) ---
  useEffect(() => {
    if (gameState?.winner && user?.id === gameState.ownerId && currentRoomId) {
        const t = setTimeout(() => {
            runTx((t, d) => {
                // [优化1]：判断是否为“对手弃牌获胜”
                // 如果是对手弃牌导致获胜，进入准备阶段时，胜利者也不自动亮牌
                const isFoldWin = d.winningHand?.description === "对手弃牌获胜";

                // [新增] 存档本局详细信息 (隐性数据)
                const historyRecord = {
                    id: Date.now(),
                    time: new Date().toLocaleString(),
                    roomId: d.roomId,
                    communityCards: d.communityCards,
                    winner: d.winner,
                    winningHand: d.winningHand,
                    // 记录所有玩家在结算时的状态（含手牌、剩余筹码、本局总下注）
                    playersSnapshot: d.players.map(p => p ? {
                        name: p.name,
                        uid: p.uid,
                        hand: p.hand,       // 关键：记录手牌
                        chips: p.chips,     // 关键：剩余筹码
                        totalBet: p.totalBet, // 关键：本局总投入
                        folded: p.folded
                    } : null)
                };
                const newHistory = [...(d.handHistory || []), historyRecord];

                // 进入 IDLE 阶段
                const ps = d.players.map(p => {
                    if (!p) return null;
                    const shouldShow = isFoldWin ? false : !p.folded; 
                    return { ...p, currentAction: null, bet: 0, hasActed: false, showCards: shouldShow };
                });

                // [优化6] 准备复盘数据，同时更新 handHistory
                t.update(null, { 
                    handHistory: newHistory, // 保存历史
                    phase: PHASE.IDLE, 
                    winner: null,
                    winningHand: null, 
                    // 这里清空 communityCards，因为它们会变成 prevCommunityCards (在 update 监听中处理了赋值)
                    communityCards: [], 
                    pot: 0, 
                    players: ps, 
                    raisesThisRound: 0, 
                    lastRaiseDiff: 0 
                }); 
            });
        }, 8000); // 8秒后自动跳转
        return () => clearTimeout(t);
    }
  }, [gameState?.winner, user, gameState?.ownerId, currentRoomId]);

  // --- UI Helpers ---
  const myPlayer = gameState?.players?.find(p => p && p.uid === user?.id);
  const isMyTurn = gameState?.currentTurnIndex !== -1 && gameState?.players[gameState?.currentTurnIndex]?.uid === user?.id;
  const currentRaises = gameState?.raisesThisRound || 0;
  const canRaise = currentRaises < MAX_RAISES;
  const mySeatIdx = gameState?.players?.findIndex(p => p && p.uid === user?.id);
  const rotationOffset = (mySeatIdx !== -1 && mySeatIdx !== undefined) ? mySeatIdx : 0;

  const adjustRaise = (delta) => {
    setRaiseAmount(prev => {
          if (!myPlayer) return prev;
          const lastDiff = gameState.lastRaiseDiff || 20;
          const minRaiseTotal = (gameState.minBet || 20) + lastDiff;
          const maxR = myPlayer.chips + myPlayer.bet;
          let next = prev + delta;
          if (next < minRaiseTotal) next = minRaiseTotal;
          if (next > maxR) next = maxR;
          return next;
    });
  };

  useEffect(() => {
      if (showRaiseControl && myPlayer) {
          const lastDiff = gameState.lastRaiseDiff || 20;
          const minRaiseTotal = (gameState.minBet || 20) + lastDiff;
          setRaiseAmount(Math.min(minRaiseTotal, myPlayer.chips + myPlayer.bet));
      }
  }, [showRaiseControl]);

  adjustRef.current = adjustRaise;
  const startPress = (delta) => {
      if (timerRef.current) return;
      if (adjustRef.current) adjustRef.current(delta);
      timerRef.current = setTimeout(() => {
          timerRef.current = setInterval(() => { if (adjustRef.current) adjustRef.current(delta); }, 100);
      }, 500);
  };
  const endPress = (e) => {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      if (timerRef.current) { clearTimeout(timerRef.current); clearInterval(timerRef.current); timerRef.current = null; }
  };
  
  const getPosLabel = (idx) => {
      if (!gameState) return null;
      const activeSeats = gameState.players.map((p,i)=>({p,i})).filter(x=>x.p);
      const activeCount = activeSeats.length;
      if (activeCount < 2) return null;
      const nextActive = (start) => { let n = (start + 1) % SEATS_COUNT; while (!gameState.players[n]) n = (n + 1) % SEATS_COUNT; return n; };
      let sb, bb, utg;
      if (activeCount === 2) { sb = gameState.dealerIndex; bb = nextActive(sb); utg = sb; }
      else { sb = nextActive(gameState.dealerIndex); bb = nextActive(sb); utg = nextActive(bb); }
      if (idx === gameState.dealerIndex) return 'D'; if (idx === sb) return 'SB'; if (idx === bb) return 'BB'; if (idx === utg && activeCount >= 3) return 'UTG'; return null;
  };

  const TurnTimer = ({ startTs, totalSec }) => {
    const [left, setLeft] = useState(totalSec);
    useEffect(() => {
        const i = setInterval(() => {
            const passed = (Date.now() - startTs) / 1000;
            const l = Math.max(0, Math.ceil(totalSec - passed));
            setLeft(l);
            if (l <= 0) clearInterval(i);
        }, 1000);
        return () => clearInterval(i);
    }, [startTs, totalSec]);
    return <div className={`text-xs font-mono ${left < 10 ? 'text-red-500 animate-pulse' : 'text-yellow-500'}`}>{left}s</div>;
  };

  if (loading) return <div className="h-screen bg-gray-950 flex items-center justify-center text-white">连接中...</div>;
  if (!currentRoomId) return <LobbyView createRoom={createRoom} joinRoom={joinRoom} loading={loading} user={user} />;
  if (!gameState) return <div className="h-screen bg-gray-950 flex items-center justify-center text-white">同步中...</div>;

  return (
    <div className="flex flex-col h-[100dvh] bg-gray-950 text-gray-100 font-sans overflow-hidden select-none relative">
      <GlobalStyles />
      {recentAction && (Date.now() - recentAction.ts < 3000) && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-50 pointer-events-none animate-in slide-in-from-top-4 fade-in duration-300">
              <div className="bg-black/70 backdrop-blur-md text-white px-6 py-2 rounded-full border border-blue-500/30 flex items-center gap-2 shadow-2xl">
                  <span className="font-bold text-blue-400">{recentAction.name}</span>
                  <span className="text-gray-300">{recentAction.type}</span>
                  {recentAction.amount > 0 && <span className="text-white font-mono">💎{recentAction.amount}</span>}
              </div>
          </div>
      )}
      
      {/* 顶部导航栏 */}
      <div className="h-14 bg-gray-900/90 backdrop-blur-md flex items-center justify-between px-4 border-b border-gray-800 shadow-md z-20">
         <div className="flex items-center gap-2">
            <div className="bg-blue-500/10 p-1.5 rounded-lg border border-blue-500/20">
               <BookOpen className="text-blue-500 w-4 h-4" />
            </div>
            <span className="font-bold text-blue-500 tracking-wider text-sm">棋牌规则学习平台</span>
            {myPlayer && (
               <button onClick={()=>runTx((t,d)=>{ 
                   if (d.phase !== PHASE.IDLE) return alert("游戏中无法离座");
                   const ps=[...d.players]; 
                   const idx=ps.findIndex(p=>p&&p.uid===user.id); 
                   if(idx!==-1) ps[idx]=null; 
                   t.update(null, { players: ps, logs: [...(d.logs||[]), `${myPlayer.name} 离座`] }); 
               })} className="ml-4 flex items-center gap-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-200 text-xs font-bold px-3 py-1.5 rounded-lg border border-red-500/30 transition-all active:scale-95 shadow-sm">
                 <LogOut size={14}/> <span>离座</span>
               </button>
            )}
         </div>
         <div className="flex items-center gap-3">
            <div className="bg-gray-800 px-3 py-1 rounded-full border border-gray-700 flex items-center gap-2">
               <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
               <span className="text-xs font-mono text-gray-300">ID: {currentRoomId}</span>
            </div>
            <button onClick={() => setCurrentRoomId(null)} className="p-2 bg-gray-800 rounded-full text-gray-400 hover:text-white"><Home size={16}/></button>
         </div>
      </div>

      {/* 中间游戏区域 */}
      <div className="flex-1 relative overflow-hidden flex flex-col bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-green-900 via-gray-900 to-black">
         <div className={`flex-1 relative transition-all duration-500 ${activeTab === 'table' ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'} flex items-center justify-center`}>
            <ZoomPanView>
                <div className="w-[92vw] aspect-[3/4] md:w-[600px] md:h-[600px] rounded-[100px] md:rounded-full bg-green-800 border-[16px] border-[#3d2b1f] shadow-[0_0_50px_rgba(0,0,0,0.8)_inset] relative box-content">
                <div className="absolute inset-0 rounded-[86px] md:rounded-full bg-[radial-gradient(circle_at_center,_#166534_0%,_#064e3b_100%)] shadow-inner"></div>
                
                {/* 筹码池显示 */}
                <div className="absolute top-[32%] left-1/2 -translate-x-1/2 flex flex-col items-center z-10 pointer-events-none">
                    <div className="bg-black/20 backdrop-blur-[2px] px-3 py-1 rounded-full border border-white/5 flex items-center gap-2">
                        <span className="text-[10px] text-gray-300 uppercase tracking-wider">Pot</span>
                        <span className="text-base font-bold text-yellow-400 flex items-center gap-1" style={{textShadow: '0 0 10px rgba(250, 204, 21, 0.5)'}}>💎 {gameState.pot}</span>
                    </div>
                </div>

                {/* 公共牌区域 [优化6 + 位置调整] */}
                {/* 逻辑：如果是复盘阶段 (IDLE)，显示 prevCommunityCards；否则显示 communityCards */}
                {/* [位置调整]: 复盘阶段将 top 设置为 60%，避开正中心的开始按钮 */}
                <div className={`absolute left-1/2 -translate-x-1/2 -translate-y-1/2 flex gap-1.5 z-10 transition-all duration-500 ${gameState.phase === PHASE.IDLE ? 'top-[60%]' : 'top-1/2'}`}>
                    {(gameState.phase === PHASE.IDLE ? prevCommunityCards : gameState.communityCards).map((c, i) => (
                        <Card key={`${i}-${c.rank}-${c.suit}`} c={c} delay={gameState.phase === PHASE.IDLE ? 0 : i * 0.3} />
                    ))}
                    {/* 复盘阶段提示 */}
                    {gameState.phase === PHASE.IDLE && prevCommunityCards.length > 0 && (
                        <div className="absolute -top-6 w-full text-center text-[10px] text-yellow-500/50 font-bold uppercase tracking-widest">上一局复盘</div>
                    )}
                    {(!gameState.communityCards.length && gameState.phase !== PHASE.IDLE) && <div className="text-green-900/40 font-bold text-xs tracking-[0.2em] animate-pulse">DEALING...</div>}
                </div>

                {/* 玩家座位渲染 */}
                {Array.from({length: SEATS_COUNT}).map((_, i) => {
                    const normalizedIndex = (i - rotationOffset + SEATS_COUNT) % SEATS_COUNT;
                    const angle = (normalizedIndex / SEATS_COUNT) * 2 * Math.PI + Math.PI / 2;
                    const top = 50 + 48 * Math.sin(angle);
                    const left = 50 + 52 * Math.cos(angle);
                    const p = gameState.players[i];
                    const active = gameState.currentTurnIndex === i;
                    const posLabel = getPosLabel(i);
                    const debtK = (gameState.ledger?.[p?.uid]?.debt || 0) / 1000;

                    let dealOrderIndex = -1;
                    let totalActivePlayers = 0;
                    if (gameState.phase === PHASE.PREFLOP && p) {
                        const dealerIdx = gameState.dealerIndex;
                        let activeSeatIndices = [];
                        for (let k = 1; k <= SEATS_COUNT; k++) {
                             const idx = (dealerIdx + k) % SEATS_COUNT;
                             if (gameState.players[idx]) activeSeatIndices.push(idx);
                        }
                        dealOrderIndex = activeSeatIndices.indexOf(i);
                        totalActivePlayers = activeSeatIndices.length;
                    }

                    return (
                        <div key={i} className="absolute w-16 h-16 -translate-x-1/2 -translate-y-1/2 transition-all duration-500 z-20" style={{ top: `${top}%`, left: `${left}%` }}>
                            {p ? (
                            <div className="relative w-full h-full">
                                {posLabel && <div className="absolute -left-4 top-1/2 -translate-y-1/2 w-6 h-4 bg-blue-600 text-white text-[9px] font-bold rounded flex items-center justify-center border border-white/20 shadow z-30">{posLabel}</div>}
                                
                                {p.isBot && gameState.ownerId === user?.id && (
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); if(window.confirm(`确认移除 ${p.name}?`)) kickBot(i); }} 
                                        className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 z-[60] border border-white/30 shadow-lg hover:bg-red-500 active:scale-90 transition-transform flex items-center justify-center"
                                        title="踢出机器人"
                                    >
                                        <X size={10} strokeWidth={3}/> 
                                    </button>
                                )}

                                {p.currentAction && (
                                    <div className="absolute bottom-[180%] left-1/2 -translate-x-1/2 mb-1 bg-white text-black px-3 py-1.5 rounded-xl shadow-lg z-50 animate-in zoom-in fade-in flex flex-col items-center justify-center min-w-[90px] border border-gray-300">
                                        {/* [修复3]：加宽 min-w 到 90px，且强制 whitespace-nowrap 不换行，确保只显示两行 */}
                                        <span className="text-[10px] font-bold leading-tight text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            {p.currentAction.type}
                                        </span>
                                        {p.currentAction.amount > 0 && (
                                            <span className="text-sm font-black leading-none text-gray-900 font-mono mt-1 whitespace-nowrap">
                                                💎{p.currentAction.amount}
                                            </span>
                                        )}
                                        {/* 小三角 */}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px] border-4 border-transparent border-t-white"></div>
                                    </div>
                                )}
                                <div className={`w-full h-full rounded-full bg-gray-800 border-2 shadow-lg flex flex-col items-center justify-center relative overflow-hidden transition-all ${active ? 'border-yellow-400 ring-4 ring-yellow-400/20 scale-110' : 'border-gray-600'} ${p.folded ? 'opacity-60 grayscale' : ''} ${p.winner ? 'ring-4 ring-green-500 animate-bounce' : ''}`}>
                                    {p.isBot && <Bot size={12} className="absolute top-1 right-1 text-purple-400"/>}
                                    {p.folded && (
                                        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                            <div className="w-full h-0.5 bg-red-600 rotate-45 shadow-sm"></div>
                                        </div>
                                    )}
                                    <User size={20} className="text-gray-500 mb-1"/>
                                    <div className="text-[9px] text-white w-full text-center truncate px-1 bg-black/40 absolute bottom-0">{p.name}</div>
                                </div>

                                {/* [修复1]：倒计时向下调整到 -bottom-14，且在复盘阶段(IDLE)强制不显示 */}
                                {active && gameState.lastAction && gameState.phase !== PHASE.IDLE && (
                                    <div className="absolute -bottom-14 left-1/2 -translate-x-1/2 bg-gray-900/80 px-2 py-1 rounded flex flex-col items-center border border-gray-700 z-50">
                                        <TurnTimer startTs={gameState.lastAction.ts} totalSec={ACTION_TIMEOUT_SEC} />
                                        <div className="text-[8px] text-gray-500 whitespace-nowrap scale-75">3分钟未动自动弃牌</div>
                                    </div>
                                )}

                                {/* [优化2]：欠款标签移动到右下角 (绿色框位置) */}
                                    {debtK > 0 && <div className="absolute -right-2 bottom-2 bg-red-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full border border-red-400 z-30 shadow-md">-{debtK}k</div>}

                                    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-gray-900 text-yellow-500 text-[9px] font-bold px-2 py-0.5 rounded-full border border-gray-700 shadow-sm whitespace-nowrap z-20">💎 {p.chips}</div>
                                {p.bet > 0 && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-600 text-white text-[9px] px-2 py-0.5 rounded-full shadow-lg border border-yellow-400 z-20 animate-in zoom-in">{p.bet}</div>}
                                
                                <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex -space-x-1 origin-bottom z-30 scale-100 transition-transform">
                                    {p.hand && p.hand.map((c, ci) => {
                                        const isMine = p.uid === user?.id;
                                        
                                        // [修复逻辑]：优化亮牌判定
                                        // 1. 如果是本人，始终可见(游戏进行中)或根据状态(复盘)
                                        // 2. 如果是其他人，只要 showCards 为 true 即可见 (允许展示弃牌 Bluff)
                                        // 3. 复盘阶段(IDLE)权限放宽
                                        const shouldShowToOthers = p.showCards; 
                                        
                                        const isVisible = isMine || shouldShowToOthers;
                                        // finalVisible 决定了渲染是背面还是正面
                                        const finalVisible = myPlayer ? isVisible : shouldShowToOthers;

                                        let dealDelay = 0;
                                        if (gameState.phase === PHASE.PREFLOP && dealOrderIndex !== -1) {
                                            dealDelay = (dealOrderIndex * 0.2) + (ci * totalActivePlayers * 0.2);
                                        }

                                        return (
                                            <Card 
                                                key={`${ci}-${c.suit}-${c.rank}`} 
                                                c={c} 
                                                hidden={!finalVisible} 
                                                small={!finalVisible} 
                                                delay={dealDelay} 
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                            ) : (
                            <button onClick={() => runTx((t, d) => { 
                                if(d.players[i]) throw "Taken"; 
                                if (d.players.some(p => p && p.uid === user.id)) throw "您已入座";
                                if (d.phase !== PHASE.IDLE) throw "请等待本局结束";
                                
                                const ledger = d.ledger || {};
                                const saved = ledger[user.id];
                                let finalName, finalChips;

                                if (saved) {
                                    finalName = saved.name;
                                    finalChips = saved.chips;
                                } else {
                                    if (!inputName || !inputName.trim()) throw "请先在菜单设置昵称";
                                    finalName = inputName.trim();
                                    finalChips = d.startingChips; 
                                    ledger[user.id] = { name: finalName, chips: finalChips, debt: 0 };
                                }
                                
                                const arr = [...d.players]; 
                                arr[i] = { uid: user.id, name: finalName, chips: finalChips, hand:[], folded:false, allIn:false, bet:0, totalBet:0, showCards:false, isBot:false, hasActed: false }; 
                                t.update(null, { players: arr, ledger }); 
                            })} className="w-full h-full rounded-full border-2 border-dashed border-white/20 flex items-center justify-center hover:bg-white/10 active:scale-95 transition-all"><span className="text-white/20 text-xs font-bold">+</span></button>
                            )}
                        </div>
                    );
                })}
                </div>
            </ZoomPanView>
            
            {/* [优化]：使用 runTx 确保亮牌状态全场同步，且忽略 folded 状态 */}
            {gameState.phase === PHASE.IDLE && myPlayer && !myPlayer.showCards && Array.isArray(myPlayer.hand) && myPlayer.hand.length === 2 && (
                <div className="absolute top-[65%] left-1/2 -translate-x-1/2 z-50">
                    <button onClick={() => runTx((t, d) => {
                        const ps = d.players.map(p => {
                            if (p && p.uid === user.id) {
                                // 强制设为 true，无论之前是否弃牌
                                return { ...p, showCards: true };
                            }
                            return p;
                        });
                        // 记录日志，让动作更明显
                        const newLog = `${myPlayer.name} 展示了底牌`;
                        t.update(null, { players: ps, logs: [...(d.logs || []), newLog] });
                    })} className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-full font-bold shadow-2xl border-2 border-indigo-400 animate-bounce flex items-center gap-2">
                        <Eye size={20}/> 亮出底牌
                    </button>
                </div>
            )}

            {/* 结算弹窗 */}
            {gameState.winner && (
               <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in pointer-events-auto">
                  <div className="bg-gray-900 p-6 rounded-2xl border border-yellow-500/50 shadow-2xl flex flex-col items-center text-center pointer-events-auto min-w-[320px] max-w-[90vw]">
                      <Trophy size={48} className="text-yellow-400 mb-4 animate-bounce"/>
                      <div className="text-yellow-500 font-bold text-xl mb-4">
                          {gameState.winner.name === "多边池结算" ? "多边池/平分结算" : "本轮结束"}
                      </div>
                      <div className="w-full space-y-2 mb-6 max-h-[40vh] overflow-y-auto">
                          {gameState.winnerDetails && gameState.winnerDetails.length > 0 ? (
                              gameState.winnerDetails.map((w, idx) => (
                                  <div key={idx} className="bg-gray-800 p-3 rounded-xl border border-gray-700 flex justify-between items-center animate-in slide-in-from-bottom-2" style={{animationDelay: `${idx * 100}ms`}}>
                                      <div className="flex flex-col items-start">
                                          <span className="font-bold text-white text-sm">{w.name}</span>
                                          <span className="text-xs text-green-400 font-mono">{w.hand}</span>
                                      </div>
                                      <div className="text-yellow-400 font-black text-lg flex items-center gap-1">
                                          <span className="text-sm">💎</span>{w.amount}
                                      </div>
                                  </div>
                              ))
                          ) : (
                              <div className="bg-gray-800 p-3 rounded-xl border border-gray-700">
                                  <div className="text-white font-bold">{gameState.winner.name}</div>
                                  <div className="text-yellow-400 font-black">💎 {gameState.winner.amount}</div>
                                  <div className="text-green-400 text-xs">{gameState.winningHand?.description || String(gameState.winningHand)}</div>
                              </div>
                          )}
                      </div>

                      {gameState.ownerId === user?.id && (
                          <button onClick={() => { 
                          runTx((t,d)=>{
                              // [新增] 存档本局详细信息 (防止手动点击漏存)
                              const historyRecord = {
                                  id: Date.now(),
                                  time: new Date().toLocaleString(),
                                  roomId: d.roomId,
                                  communityCards: d.communityCards,
                                  winner: d.winner,
                                  winningHand: d.winningHand,
                                  playersSnapshot: d.players.map(p => p ? {
                                      name: p.name, uid: p.uid, hand: p.hand, chips: p.chips, totalBet: p.totalBet, folded: p.folded
                                  } : null)
                              };
                              const newHistory = [...(d.handHistory || []), historyRecord];

                              // [优化1]：手动点击下一轮时，同样应用“弃牌获胜不亮牌”逻辑
                              const isFoldWin = d.winningHand?.description === "对手弃牌获胜";

                              const ps = d.players.map(p => {
                                  if (!p) return null;
                                  // 如果是弃牌获胜，shouldShow 为 false；否则为 !p.folded
                                  const shouldShow = isFoldWin ? false : !p.folded;
                                  return { ...p, currentAction: null, bet: 0, hasActed: false, showCards: shouldShow };
                              });
                              const currentConfig = d.blindConfig || 20;
                              t.update(null, { 
                                  handHistory: newHistory, // 保存历史
                                  phase: PHASE.IDLE, 
                                  winner: null,
                                  winnerDetails: null, 
                                  winningHand: null, 
                                  communityCards: [], 
                                  pot: 0, 
                                  players: ps, 
                                  raisesThisRound: 0, 
                                  lastRaiseDiff: 0,
                                  minBet: currentConfig 
                              }); 
                          });
                          }} className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-3 rounded-xl font-bold text-lg shadow-lg active:scale-95 transition-transform w-full">
                              下一轮学习
                          </button>
                      )}
                  </div>
               </div>
            )}
            
            {/* [优化6]：删除了原来的“上一局复盘”小窗口，因为已经集成到中心区域显示 */}

            {/* 房主开始游戏按钮 (修复偶发性发牌两次) */}
            {gameState.ownerId === user?.id && gameState.phase === PHASE.IDLE && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-auto">
                    <button onClick={() => runTx((t,d) => {
                        // [优化3]：防止并发导致的发牌两次
                        if (d.phase !== PHASE.IDLE) return; 

                        if (!d.initialChipsSet) return alert("请先在菜单中设置基础筹码");
                        const playersWithChips = d.players.filter(p => p && p.chips > 0);
                        if(playersWithChips.length < 2) return alert("需要至少2人持有筹码");

                        const deck = generateDeck();
                        const ps = d.players.map(p => {
                            if (!p) return null; 
                            const resetPlayer = {
                                ...p, bet: 0, totalBet: 0, currentAction: null, hasActed: false, showCards: false, winner: false, allIn: false 
                            };
                            if (p.chips > 0) {
                                return { ...resetPlayer, hand: [deck.pop(), deck.pop()], folded: false };
                            } else {
                                return { ...resetPlayer, hand: [], folded: true };
                            }
                        });

                        const getNextActive = (startIdx) => {
                             let n = (startIdx + 1) % SEATS_COUNT;
                             let count = 0;
                             while ((!ps[n] || ps[n].chips <= 0) && count < SEATS_COUNT) { n = (n + 1) % SEATS_COUNT; count++; }
                             return n;
                        };

                        let dlr = getNextActive(d.dealerIndex);
                        let sb = getNextActive(dlr);
                        let bb = getNextActive(sb);
                        let utg = getNextActive(bb);
                        if (playersWithChips.length === 2) { sb = dlr; bb = getNextActive(sb); utg = sb; }
                        
                        let curBlind = d.blindConfig || 20; 
                        const post = (ix, v) => {
                             const p = ps[ix];
                             let r = Math.min(p.chips, v);
                             p.chips -= r; p.bet = r; p.totalBet = r; 
                             if(p.chips === 0) p.allIn = true;
                             return r;
                        };

                        const pot = post(sb, curBlind/2) + post(bb, curBlind);
                        const lastAction = { uid: 'system', name: 'System', type: 'Blinds', amount: 0, ts: Date.now() };

                        t.update(null, { 
                            deck, players: ps, communityCards:[], pot, 
                            dealerIndex:dlr, currentTurnIndex:utg, 
                            phase: PHASE.PREFLOP, actionsThisRound: 0, raisesThisRound: 0, 
                            lastRaiseDiff: curBlind, 
                            winner:null, logs: [...(d.logs||[]), 'New Hand'], 
                            minBet: curBlind, 
                            lastAction 
                        });
                    })} className="bg-blue-600 text-white px-8 py-3 rounded-full font-bold shadow-[0_0_20px_rgba(37,99,235,0.6)] animate-pulse hover:scale-105 transition-transform">开始发牌</button>
                </div>
            )}
            
         </div>
         {activeTab === 'bank' && <BankView gameState={gameState} user={user} runTx={runTx} myPlayer={myPlayer} currentRoomId={currentRoomId} />}
         {activeTab === 'settings' && <SettingsView gameState={gameState} user={user} setInputName={setInputName} inputName={inputName} runTx={runTx} myPlayer={myPlayer} closeRoom={closeRoom} volumes={volumes} setVolumes={setVolumes} setShowSoundMenu={setShowSoundMenu} />}
      </div>
      
      {showSoundMenu && (
        // ... (声音菜单保持不变) ...
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6 animate-in fade-in" onClick={()=>setShowSoundMenu(false)}>
            <div className="bg-gray-900 border border-gray-700 w-full max-w-sm rounded-2xl p-6 shadow-2xl" onClick={e=>e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2"><Music className="text-yellow-500"/> 声音设置</h3>
                    <button onClick={()=>setShowSoundMenu(false)}><X className="text-gray-400"/></button>
                </div>
                <div className="space-y-6">
                    <div className="space-y-2"><div className="flex justify-between text-xs text-gray-400 uppercase font-bold"><span>背景音乐</span><span>{Math.round(volumes.bgm * 100)}%</span></div><div className="flex items-center gap-3"><Music size={16} className="text-gray-500"/><input type="range" min="0" max="1" step="0.05" value={volumes.bgm} onChange={(e)=>setVolumes({...volumes, bgm: parseFloat(e.target.value)})} /></div></div>
                    <div className="space-y-2"><div className="flex justify-between text-xs text-gray-400 uppercase font-bold"><span>游戏音效</span><span>{Math.round(volumes.sfx * 100)}%</span></div><div className="flex items-center gap-3"><Volume2 size={16} className="text-gray-500"/><input type="range" min="0" max="1" step="0.05" value={volumes.sfx} onChange={(e)=>setVolumes({...volumes, sfx: parseFloat(e.target.value)})} /></div></div>
                    <div className="space-y-2"><div className="flex justify-between text-xs text-gray-400 uppercase font-bold"><span>语音输入</span><span>{Math.round(volumes.mic * 100)}%</span></div><div className="flex items-center gap-3"><Mic size={16} className="text-gray-500"/><input type="range" min="0" max="1" step="0.05" value={volumes.mic} onChange={(e)=>setVolumes({...volumes, mic: parseFloat(e.target.value)})} /></div></div>
                    <div className="space-y-2"><div className="flex justify-between text-xs text-gray-400 uppercase font-bold"><span>语音输出</span><span>{Math.round(volumes.speaker * 100)}%</span></div><div className="flex items-center gap-3"><Speaker size={16} className="text-gray-500"/><input type="range" min="0" max="1" step="0.05" value={volumes.speaker} onChange={(e)=>setVolumes({...volumes, speaker: parseFloat(e.target.value)})} /></div></div>
                </div>
                <button onClick={()=>setShowSoundMenu(false)} className="w-full bg-gray-800 hover:bg-gray-700 py-3 rounded-xl mt-8 font-bold text-gray-300">关闭</button>
            </div>
        </div>
      )}

      <FloatingChat user={user} gameState={gameState} runTx={runTx} myPlayer={myPlayer} volumes={volumes} isMicOn={isMicOn} toggleMic={toggleMic} />

      {/* 底部操作栏 */}
      <div className="h-auto min-h-[140px] bg-gray-900 border-t border-gray-800 flex flex-col z-30 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
         <div className="flex-1 p-4 flex items-center justify-center gap-3 relative">
            {/* [优化2] 复盘阶段隐藏按钮改为提示文字 */}
            {gameState.phase === PHASE.IDLE ? (
                <div className="text-yellow-500/80 font-mono animate-pulse tracking-widest text-sm">
                    等待房主开始发牌......
                </div>
            ) : (
                myPlayer && isMyTurn ? (
                    showRaiseControl ? (
                       <div className="w-full flex flex-col gap-3 animate-in slide-in-from-bottom duration-300">
                          <div className="flex items-center justify-between bg-gray-800 p-3 rounded-xl border border-gray-700">
                             <button onMouseDown={()=>startPress(-20)} onTouchStart={()=>startPress(-20)} onMouseUp={endPress} onMouseLeave={endPress} onTouchEnd={endPress} onContextMenu={(e)=>e.preventDefault()} className="p-2 bg-gray-700 rounded-lg select-none active:bg-gray-600 active:scale-95 transition-all touch-manipulation touch-none"><ChevronDown/></button>
                             <div className="text-xl font-bold text-yellow-400 select-none">💎 {raiseAmount}</div>
                             <button onMouseDown={()=>startPress(20)} onTouchStart={()=>startPress(20)} onMouseUp={endPress} onMouseLeave={endPress} onTouchEnd={endPress} onContextMenu={(e)=>e.preventDefault()} className="p-2 bg-gray-700 rounded-lg select-none active:bg-gray-600 active:scale-95 transition-all touch-manipulation touch-none"><ChevronUp/></button>
                          </div>
                          <div className="flex gap-2">
                             <button onClick={()=>setRaiseAmount(Math.floor(Math.max((gameState.minBet||20)*2, gameState.pot*0.5)))} className="flex-1 bg-gray-700 py-2 rounded text-xs text-yellow-300">1/2 池</button>
                             <button onClick={()=>setRaiseAmount(Math.floor(Math.max((gameState.minBet||20)*2, gameState.pot)))} className="flex-1 bg-gray-700 py-2 rounded text-xs text-yellow-300">满池</button>
                             <button onClick={()=>setRaiseAmount(myPlayer.chips+myPlayer.bet)} className="flex-1 bg-red-900/50 border border-red-500/50 py-2 rounded text-xs text-red-300">ALL IN</button>
                          </div>
                          <div className="flex gap-2">
                             <button onClick={()=>setShowRaiseControl(false)} className="flex-1 bg-gray-700 py-3 rounded-xl font-bold text-gray-300">取消</button>
                             <button onClick={()=>handleAction('raise', raiseAmount)} className="flex-[2] bg-yellow-600 hover:bg-yellow-500 py-3 rounded-xl font-bold text-white shadow-lg">确认加注</button>
                          </div>
                       </div>
                    ) : (
                       <>
                           <GameBtn label="弃牌" sub="" color="red" onClick={()=>handleAction('fold')} />
                           <GameBtn label={gameState.minBet > myPlayer.bet ? "跟注" : "过牌"} sub={gameState.minBet > myPlayer.bet ? `💎${gameState.minBet - myPlayer.bet}` : ""} color="blue" onClick={()=>handleAction(gameState.minBet > myPlayer.bet ? 'call' : 'check')} />
                           <GameBtn label={canRaise ? "加注" : "加注封顶"} sub={canRaise ? `还可加${MAX_RAISES-currentRaises}次` : "Max"} color="green" onClick={()=>canRaise && setShowRaiseControl(true)} disabled={!canRaise}/>
                       </>
                    )
                 ) : (
                    <div className="text-gray-500 text-sm italic">
                       {!myPlayer ? "请先在上方设置昵称并入座" : "等待其他玩家行动..."}
                    </div>
                 )
            )}
            
            {myPlayer && (
               <div className="absolute right-4 top-2 flex flex-col gap-2">
                  {myPlayer.folded && !myPlayer.showCards && (
                     <button onClick={()=>simpleUpdate(`players`, gameState.players.map(p => (p && p.uid === user.id) ? {...p, showCards: true} : p))} className="text-[10px] bg-gray-800 border border-gray-600 px-2 py-1 rounded text-gray-300">亮牌</button>
                  )}
               </div>
            )}
         </div>
         <div className="h-16 bg-black flex items-center justify-around border-t border-gray-800 pb-2">
            <NavBtn icon={Activity} label="牌桌" active={activeTab==='table'} onClick={()=>setActiveTab('table')} />
            <NavBtn icon={CreditCard} label="银行" active={activeTab==='bank'} onClick={()=>setActiveTab('bank')} />
            <NavBtn icon={Menu} label="菜单" active={activeTab==='settings'} onClick={()=>setActiveTab('settings')} />
         </div>
      </div>
    </div>
  );
}

// --- 5. 子视图组件 ---

function LobbyView({ createRoom, joinRoom, loading, user }) { 
    const [code, setCode] = useState('');
    return (
        <div className="h-[100dvh] w-full bg-gray-950 flex flex-col items-center justify-center p-6 text-white bg-[radial-gradient(ellipse_at_top,_#1e293b_0%,_#020617_100%)] overflow-hidden relative touch-none">
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none"/>
            <div className="mb-12 text-center animate-in slide-in-from-top duration-700 z-10 flex flex-col items-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-3xl flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(59,130,246,0.3)] border border-blue-400/20">
                    <BookOpen className="w-10 h-10 text-white drop-shadow-md"/>
                </div>
                <h1 className="text-3xl font-black text-white mb-2 tracking-tighter">棋牌规则学习平台</h1>
                <p className="text-gray-500 text-xs">本平台仅作为学习使用，禁止用于赌博用途</p>
            </div>

            <div className="w-full max-w-xs space-y-6 animate-in slide-in-from-bottom duration-700 delay-100 z-10">
                <div className="space-y-2">
                    <label className="text-xs text-gray-500 font-bold ml-1 uppercase">加入现有学习室</label>
                    <div className="flex gap-2">
                        <input 
                            value={code} 
                            onChange={e=>setCode(e.target.value)} 
                            placeholder="6位房间号" 
                            className="flex-1 bg-gray-900/50 border border-gray-700 rounded-2xl px-4 py-4 text-center text-xl font-mono tracking-widest text-white placeholder:text-gray-700 outline-none focus:border-blue-500 focus:bg-gray-900 transition-all" 
                            maxLength={6}
                        />
                        <button 
                            onClick={()=>joinRoom(code)} 
                            disabled={!user || code.length !== 6} 
                            className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-16 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-900/20 transition-all active:scale-95"
                        >
                            <LogIn className="w-6 h-6"/>
                        </button>
                    </div>
                </div>

                <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-800"></div></div>
                    <div className="relative flex justify-center"><span className="bg-gray-950 px-3 text-gray-600 text-xs font-bold">OR</span></div>
                </div>

                <button 
                    onClick={createRoom} 
                    disabled={!user} 
                    className={`w-full bg-gray-800 hover:bg-gray-700 text-white font-bold py-4 rounded-2xl border border-gray-700 active:scale-95 transition-all flex items-center justify-center gap-2 ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {user ? <><Plus size={20} strokeWidth={3}/> 创建新房间</> : <span className="animate-pulse">正在连接...</span>}
                </button>
            </div>
        </div>
    );
}

function FloatingChat({ user, gameState, runTx, myPlayer, volumes, isMicOn, toggleMic }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [pos, setPos] = useState({ x: 20, y: 100 });
  const [drag, setDrag] = useState(false);
  const [input, setInput] = useState('');
  const [showBubble, setShowBubble] = useState(false);
  const ref = useRef(null);
  const offset = useRef({ x: 0, y: 0 });
  
  // [新增] 长按下载相关 Ref
  const pressTimer = useRef(null);

  // 拖拽逻辑
  const handleDown = (e) => {
    setDrag(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    offset.current = { x: clientX - pos.x, y: clientY - pos.y };
  };
  
  const handleMove = (e) => {
    if (!drag) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setPos({ x: clientX - offset.current.x, y: clientY - offset.current.y });
  };
  
  const handleUp = () => setDrag(false);

  useEffect(() => {
    if (drag) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [drag]);

  // 消息提示气泡逻辑
  const msgs = gameState?.messages || [];
  const lastMsg = msgs[msgs.length - 1];

  useEffect(() => {
    if (lastMsg && !isOpen && Date.now() - lastMsg.ts < 60000) {
      setShowBubble(true);
      const t = setTimeout(() => setShowBubble(false), 60000);
      return () => clearTimeout(t);
    }
    if (isOpen) setShowBubble(false);
  }, [lastMsg, isOpen]);

  // 发送消息
  const sendMsg = () => {
    if (!input.trim() || !myPlayer) return;
    runTx((t, d) => {
      const newMsg = { id: Date.now(), text: input, name: myPlayer.name, uid: user.id, ts: Date.now() };
      const updated = [...(d.messages || []), newMsg].slice(-50);
      t.update(null, { messages: updated });
    });
    setInput('');
  };

  // [升级] 导出全量详细隐性数据
  const downloadGameData = () => {
      if (!gameState) return;
      const dateStr = new Date().toLocaleString();
      let content = `=== 德州扑克完整战报 (管理员版) ===\n`;
      content += `导出时间: ${dateStr}\n`;
      content += `房间ID: ${gameState.roomId}\n`;
      content += `===================================\n\n`;

      // 1. 导出历史对局详情
      const history = gameState.handHistory || [];
      if (history.length > 0) {
          content += `【历史对局回放 (共 ${history.length} 局)】\n\n`;
          history.forEach((game, index) => {
              content += `--- 第 ${index + 1} 局 (${game.time}) ---\n`;
              content += `公共牌: ${game.communityCards.map(c => c.suit+c.rank).join(' ') || "无"}\n`;
              content += `赢家: ${game.winner?.name} (赢取 💎${game.winner?.amount})\n`;
              content += `赢牌牌型: ${game.winningHand?.description}\n`;
              content += `>> 玩家详情 (含暗牌):\n`;
              
              (game.playersSnapshot || []).forEach(p => {
                  if(p) {
                      const handStr = p.hand.map(c => c.suit+c.rank).join(' ');
                      content += `   [${p.name}] 手牌: [${handStr}] | 本局下注: ${p.totalBet} | 剩余: ${p.chips} | ${p.folded ? '已弃牌' : '存活'}\n`;
                  }
              });
              content += `\n`;
          });
      } else {
          content += `(暂无历史对局记录)\n\n`;
      }

      // 2. 导出本局公开战报日志
      content += `===================================\n`;
      content += `【本场公开战报日志】\n`;
      (gameState.logs || []).forEach(log => {
          content += `${log}\n`;
      });

      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Poker_Full_Data_${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      alert("🗂️ 完整数据已导出 (含隐藏底牌信息)");
  };

  // [新增] 长按处理逻辑
  const startPress = () => {
      pressTimer.current = setTimeout(() => {
          downloadGameData();
          pressTimer.current = null;
      }, 3000); // 3秒触发
  };

  const cancelPress = () => {
      if (pressTimer.current) {
          clearTimeout(pressTimer.current);
          pressTimer.current = null;
      }
  };

  // 渲染：展开状态
  if (isOpen) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setIsOpen(false)}>
        <div className="bg-gray-900 w-full max-w-sm rounded-2xl border border-gray-700 overflow-hidden shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
          <div className="bg-gray-800 p-3 flex justify-between items-center border-b border-gray-700">
            <div className="flex gap-4">
              <button onClick={() => setActiveTab('chat')} className={`font-bold flex items-center gap-2 ${activeTab === 'chat' ? 'text-white' : 'text-gray-500'}`}><MessageCircle size={18} /> 聊天</button>
              
              {/* [修改] 战报按钮：增加长按事件 */}
              <button 
                onClick={() => setActiveTab('logs')} 
                onMouseDown={startPress} 
                onMouseUp={cancelPress} 
                onMouseLeave={cancelPress}
                onTouchStart={startPress}
                onTouchEnd={cancelPress}
                className={`font-bold flex items-center gap-2 ${activeTab === 'logs' ? 'text-white' : 'text-gray-500'} select-none transition-colors duration-200 active:scale-95`}
                title="长按3秒导出数据"
              >
                  <Layers size={18} /> 战报
              </button>

            </div>
            <button onClick={() => setIsOpen(false)}><X className="text-gray-400" /></button>
          </div>
          {activeTab === 'chat' ? (
            <>
              <div className="h-64 overflow-y-auto p-4 space-y-3 bg-gray-950/50">
                {msgs.slice(-8).map((m) => (
                  <div key={m.id} className={`flex flex-col ${m.uid === user.id ? 'items-end' : 'items-start'}`}>
                    <span className="text-[10px] text-gray-500 mb-0.5">{m.name}</span>
                    <div className={`px-3 py-2 rounded-xl text-sm max-w-[80%] ${m.uid === user.id ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-gray-800 text-gray-200 rounded-tl-none'}`}> {m.text} </div>
                  </div>
                ))}
                {msgs.length === 0 && <div className="text-center text-gray-600 text-xs mt-4">暂无消息</div>}
              </div>
              <div className="p-3 bg-gray-800 border-t border-gray-700 flex gap-2">
                <input value={input} onChange={e => setInput(e.target.value)} className="flex-1 bg-gray-900 border border-gray-600 rounded-full px-4 text-sm text-white outline-none focus:border-blue-500" placeholder="说点什么..." onKeyDown={e => e.key === 'Enter' && sendMsg()} />
                <button onClick={sendMsg} className="bg-blue-600 p-2 rounded-full text-white hover:bg-blue-500"><Send size={18} /></button>
              </div>
            </>
          ) : (
            <div className="h-80 overflow-y-auto p-4 space-y-2 bg-gray-950/50">
              {[...gameState.logs].reverse().map((l, i) => (
                <div key={i} className="text-xs text-gray-300 border-b border-gray-800 pb-1">{String(l)}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // 渲染：悬浮球状态
  return (
    <div ref={ref} className="fixed z-40 touch-none cursor-move" style={{ left: pos.x, top: pos.y }} onMouseDown={handleDown} onTouchStart={handleDown}>
      <div className="relative flex flex-col items-center gap-3">
        {showBubble && lastMsg && (
          <div className="absolute right-full mr-2 top-0 bg-white text-black text-xs px-3 py-2 rounded-xl rounded-tr-none shadow-lg w-max max-w-[150px] truncate animate-in zoom-in slide-in-from-right-2 pointer-events-none">
            <span className="font-bold text-blue-600">{lastMsg.name}:</span> {lastMsg.text}
          </div>
        )}
        
        {/* 聊天按钮 */}
        <button onClick={() => !drag && setIsOpen(true)} className="w-12 h-12 bg-blue-600/90 backdrop-blur rounded-full shadow-lg border-2 border-white/20 flex items-center justify-center text-white hover:scale-105 active:scale-95 transition-transform">
          <MessageCircle size={24} />
        </button>
        
        {/* 麦克风按钮 */}
        <button
          onClick={(e) => { e.stopPropagation(); if (toggleMic) toggleMic(); }}
          className={`w-8 h-8 rounded-full shadow-lg border border-white/10 flex items-center justify-center transition-colors ${isMicOn ? 'bg-green-600 text-white animate-pulse' : 'bg-gray-800 text-gray-400'}`}
        >
          {isMicOn ? <Mic size={14} /> : <MicOff size={14} />}
        </button>
      </div>
    </div>
  );
}

// --- BankView 和 SettingsView 之前的代码保持不变，确保这里不再有重复的 FloatingChat ---

function BankView({ gameState, user, runTx, myPlayer, currentRoomId }) {
  const [loanAmount, setLoanAmount] = useState(1000);
  const isOwner = gameState.ownerId === user.id;

  const approveAll = () => {
    runTx((t, d) => {
      const ls = [...d.bank.loans];
      const ps = [...d.players];
      const ledger = d.ledger || {};
      const history = d.bank.history || [];
      const addedHistory = [];
      
      ls.forEach(l => {
        const pIdx = ps.findIndex(x => x && x.uid === l.requesterId);
        if (pIdx !== -1) {
          ps[pIdx].chips += l.amount;
          if(ledger[l.requesterId]) {
            ledger[l.requesterId].debt = (ledger[l.requesterId].debt || 0) + l.amount;
            ledger[l.requesterId].chips = ps[pIdx].chips;
          }
          addedHistory.push({ name: l.requesterName, amount: l.amount, type: 'approve', ts: Date.now() });
        }
      });
      t.update(null, { 'bank.loans': [], 'bank.history': [...history, ...addedHistory], players: ps, ledger });
    });
  };

  return (
    <div className="absolute inset-0 bg-gray-900/95 backdrop-blur z-20 p-6 overflow-y-auto animate-in fade-in slide-in-from-bottom-10">
      <h2 className="text-2xl font-bold text-blue-500 mb-6 flex items-center gap-2"><CreditCard /> 公共积分银行</h2>
      
      <div className="bg-gradient-to-br from-gray-800 to-black p-6 rounded-2xl border border-gray-700 mb-6 shadow-xl text-center">
        <div className="text-gray-400 text-sm uppercase tracking-widest mb-2">Infinite Supply</div>
        <div className="text-4xl font-black text-blue-400 mb-4">∞</div>
        {myPlayer && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center bg-gray-900 rounded-lg p-1 border border-gray-700">
              <span className="pl-3 text-xs text-gray-500">金额:</span>
              <input type="number" value={loanAmount} onChange={e => setLoanAmount(Number(e.target.value))} className="flex-1 bg-transparent text-white p-2 text-center outline-none font-mono" />
            </div>
            <button onClick={() => runTx((t, d) => {
              if (d.bank.loans.some(l => l.requesterId === user.id)) throw "Pending";
              t.update(null, { 'bank.loans': [...d.bank.loans, { requesterId: user.id, requesterName: myPlayer.name, amount: loanAmount, votes: {}, ts: Date.now() }] });
            })} className="w-full bg-blue-600 hover:bg-blue-500 text-white py-3 rounded-xl font-bold shadow-lg active:scale-95 transition-transform"> 申请借款 </button>
          </div>
        )}
      </div>

      {isOwner && gameState.bank.loans.length > 0 && (
        <button onClick={approveAll} className="w-full mb-6 bg-green-600 py-3 rounded-xl font-bold text-white shadow-lg">一键全部批准</button>
      )}

      <div className="space-y-3 mb-8">
        <h3 className="text-gray-500 text-xs font-bold uppercase mb-2">待审批 ({gameState.bank.loans.length})</h3>
        {gameState.bank.loans.map((l, i) => (
          <div key={i} className="bg-gray-800 p-4 rounded-xl border-l-4 border-blue-500 shadow-md relative">
            <div className="flex justify-between items-center mb-3"> <span className="font-bold text-white">{l.requesterName}</span> <span className="text-yellow-400 font-mono font-bold">💎{l.amount}</span> </div>
            {myPlayer && l.requesterId !== user.id && l.votes[user.id] === undefined ? (
              <div className="flex gap-2">
                <button onClick={() => voteLoan(currentRoomId, l.requesterId, true, user.id, user.id, runTx)} className="flex-1 bg-green-600 py-2 rounded-lg text-sm font-bold">同意</button>
                <button onClick={() => voteLoan(currentRoomId, l.requesterId, false, user.id, user.id, runTx)} className="flex-1 bg-red-600 py-2 rounded-lg text-sm font-bold">拒绝</button>
              </div>
            ) : (<div className="text-center bg-black/20 py-2 rounded-lg text-xs text-gray-400"> {l.requesterId === user.id ? '等待审批...' : '已投票'} </div>)}
            <div className="mt-2 h-1 bg-gray-700 rounded-full overflow-hidden flex"> <div style={{ width: `${(Object.values(l.votes).filter(v => v).length / (gameState.players.filter(p => p).length || 1)) * 100}%` }} className="bg-green-500 h-full"></div> <div style={{ width: `${(Object.values(l.votes).filter(v => !v).length / (gameState.players.filter(p => p).length || 1)) * 100}%` }} className="bg-red-500 h-full"></div> </div>
          </div>
        ))}
        {!gameState.bank.loans.length && <div className="text-gray-600 text-center py-4 text-xs">暂无申请</div>}
      </div>

      <div className="space-y-2">
        <h3 className="text-gray-500 text-xs font-bold uppercase">借款历史明细</h3>
        {(gameState.bank.history || []).slice(-10).reverse().map((h, i) => (
          <div key={i} className="flex justify-between text-xs text-gray-400 border-b border-gray-800 pb-1">
            <span>{h.name}</span>
            <span className="text-green-500">+{h.amount}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsView({ gameState, user, setInputName, inputName, runTx, myPlayer, closeRoom, volumes, setVolumes, setShowSoundMenu }) {
  const isOwner = gameState.ownerId === user?.id;
  const [setupChips, setSetupChips] = useState(1000);
  const hasRecord = !!gameState.ledger?.[user.id];
  const isBlindLocked = gameState.blindLocked || false;

  return (
    <div className="absolute inset-0 bg-gray-900/95 backdrop-blur z-20 p-6 overflow-y-auto animate-in fade-in slide-in-from-bottom-10">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2"><Settings /> 菜单</h2>
      
      {/* 声音设置按钮 */}
      <button onClick={() => setShowSoundMenu(true)} className="w-full bg-gray-800 hover:bg-gray-700 mb-6 p-4 rounded-xl border border-gray-600 flex justify-between items-center group transition-colors">
        <div className="flex items-center gap-3">
          <div className="bg-yellow-500/10 p-2 rounded-lg group-hover:bg-yellow-500/20 transition-colors"><Music size={20} className="text-yellow-500" /></div>
          <span className="font-bold text-gray-200">声音与音效</span>
        </div>
        <span className="text-gray-500 text-sm">点击设置 &gt;</span>
      </button>

      <div className="bg-gray-800 p-4 rounded-xl mb-6 border border-gray-700">
        <label className="text-xs text-gray-400 block mb-1">我的昵称</label>
        <div className="flex gap-2">
          <input
            value={hasRecord ? gameState.ledger[user.id].name : inputName}
            onChange={e => !hasRecord && setInputName(e.target.value)}
            disabled={hasRecord}
            className="flex-1 bg-black/30 border border-gray-600 rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500 transition-colors disabled:opacity-50"
            placeholder={hasRecord ? "昵称已锁定" : "输入昵称以入座..."}
          />
        </div>
        {!hasRecord && <div className="text-xs text-green-400 mt-2">* 请输入昵称，一旦入座将无法修改</div>}
        {hasRecord && <div className="text-xs text-gray-500 mt-2">* 昵称已绑定账号</div>}
      </div>

      <div className="bg-gray-800 p-4 rounded-xl mb-6 border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm font-bold text-gray-300">管理员权限</span>
          {isOwner ? <span className="text-xs bg-yellow-500/20 text-yellow-500 px-2 py-1 rounded">你是房主</span> : (myPlayer && <button onClick={() => runTx((t, d) => t.update(null, { ownerId: user.id, logs: [...(d.logs || []), `${myPlayer.name} 成为管理员`] }))} className="text-xs bg-gray-700 px-2 py-1 rounded hover:bg-gray-600">申请成为房主</button>)}
        </div>
        {isOwner && (
          <div className="space-y-3">
            {!gameState.initialChipsSet ? (
              <div className="bg-black/30 p-3 rounded-lg border border-yellow-500/30">
                <label className="text-xs text-yellow-400 font-bold block mb-2 flex items-center gap-1"><Coins size={12} /> 设置初始筹码 (仅一次)</label>
                <div className="flex gap-2">
                  <input type="number" value={setupChips} onChange={e => setSetupChips(Number(e.target.value))} className="flex-1 bg-gray-900 border border-gray-600 rounded text-white px-2 py-1 text-sm outline-none font-mono" />
                  <button onClick={() => runTx((t, d) => {
                    if (d.initialChipsSet) return;
                    const ps = [...d.players];
                    const ledger = d.ledger || {};
                    const updatedPs = ps.map(p => {
                      if (p) {
                        p.chips = setupChips;
                        if (ledger[p.uid]) ledger[p.uid].chips = setupChips;
                        return p;
                      }
                      return null;
                    });
                    t.update(null, {
                      startingChips: setupChips,
                      initialChipsSet: true,
                      players: updatedPs,
                      ledger: ledger,
                      logs: [...(d.logs || []), `💰 房主设置初始筹码: ${setupChips}`]
                    });
                  })} className="bg-yellow-600 text-white px-3 py-1 rounded text-xs font-bold whitespace-nowrap">确认发放</button>
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-500 flex items-center gap-2"><Coins size={12} /> 初始筹码已设定: {gameState.startingChips}</div>
            )}

            <button onClick={() => runTx((t, d) => { const idx = d.players.findIndex(p => p === null); if (idx === -1) throw "Full"; const ps = [...d.players]; ps[idx] = { uid: `bot-${Date.now()}`, name: `Bot ${Math.floor(Math.random() * 99)}`, chips: d.startingChips, hand: [], folded: false, allIn: false, bet: 0, totalBet: 0, showCards: false, isBot: true, hasActed: false }; t.update(null, { players: ps }); })} className="w-full bg-purple-600 hover:bg-purple-500 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2"> <Bot size={16} /> 添加 AI 陪练 </button>
            
            {/* 盲注设置区域 */}
            <div className="flex items-center gap-2 bg-black/20 p-2 rounded-lg">
                <span className="text-xs text-gray-400 whitespace-nowrap">大盲注(BB):</span>
                <input 
                    type="number" 
                    disabled={isBlindLocked}
                    // [修复]：回显优先显示 blindConfig，如果没有则显示 minBet
                    defaultValue={gameState.blindConfig || gameState.minBet || 20} 
                    onBlur={e => {
                        if(!isBlindLocked) {
                            const val = Number(e.target.value);
                            // [修复]：同时更新 minBet (当前显示) 和 blindConfig (永久配置)
                            runTx((t, d) => t.update(null, { 
                                minBet: val, 
                                blindConfig: val, 
                                logs: [...(d.logs || []), `房主调整盲注为: ${val}`] 
                            }));
                        }
                    }} 
                    className={`flex-1 bg-transparent text-right font-mono outline-none ${isBlindLocked ? 'text-gray-500' : 'text-yellow-400'}`} 
                />
                {/* 锁定按钮代码保持不变... */}
                <button 
                // ... (省略原有按钮代码)
                >
                    {isBlindLocked ? <div className="text-[10px] font-bold">已锁</div> : <Settings size={14}/>}
                </button>
            </div>

            <button onClick={closeRoom} className="w-full bg-red-900/50 hover:bg-red-900 border border-red-800 py-3 rounded-lg font-bold text-sm text-red-200 flex items-center justify-center gap-2"> <Power size={16} /> 关闭房间 </button>
          </div>
        )}
      </div>
      <h3 className="text-gray-400 text-xs font-bold uppercase mb-2">系统日志</h3>
      <div className="bg-black/40 rounded-xl p-3 h-48 overflow-y-auto text-xs space-y-1.5 font-mono text-gray-300 border border-white/5">
        {[...gameState.logs].reverse().map((l, i) => (<div key={i} className="border-b border-white/5 pb-1">{String(l)}</div>))}
      </div>
    </div>
  );
}

// --- Card 组件 ---
function Card({ c, hidden, small, delay = 0 }) {
  if (!c) return <div className="w-8"></div>;

  const animClass = "card-deal-anim";
  const animStyle = {
    animationDelay: `${delay}s`,
    animationFillMode: 'forwards'
  };

  if (hidden) return (
    <div
      style={animStyle}
      className={`rounded bg-blue-900 border border-blue-700 shadow-lg ${small ? 'w-8 h-12' : 'w-11 h-16'} bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-800 to-black ${animClass}`}
    ></div>
  );

  const isRed = ['♥', '♦'].includes(c.suit);

  return (
    <div
      style={animStyle}
      className={`bg-gray-100 rounded shadow-xl flex flex-col items-center justify-center border border-gray-300 relative ${small ? 'w-8 h-12 text-xs' : 'w-11 h-16 text-sm'} ${animClass}`}
    >
      <span className={`font-black ${isRed ? 'text-red-600' : 'text-black'}`}>{c.rank}</span>
      <span className={`${isRed ? 'text-red-600' : 'text-black'} text-lg leading-none`}>{c.suit}</span>
    </div>
  );
}

function GameBtn({ label, sub, color, onClick, disabled }) {
  const style = { red: 'bg-gradient-to-b from-red-500 to-red-700 shadow-red-900/50', blue: 'bg-gradient-to-b from-blue-500 to-blue-700 shadow-blue-900/50', green: 'bg-gradient-to-b from-emerald-500 to-emerald-700 shadow-emerald-900/50', }[color];
  return (<button onClick={onClick} disabled={disabled} className={`flex-1 ${disabled ? 'bg-gray-700 opacity-50 cursor-not-allowed' : style} text-white rounded-xl py-3 shadow-lg active:scale-95 transition-all flex flex-col items-center justify-center border-t border-white/20`}> <span className="font-bold text-sm md:text-base leading-none">{label}</span> {sub && <span className="text-[10px] opacity-80 mt-1 font-mono">{sub}</span>} </button>);
}

function NavBtn({ icon: Icon, label, active, onClick }) { return (<button onClick={onClick} className={`flex flex-col items-center justify-center w-16 transition-colors ${active ? 'text-yellow-400' : 'text-gray-500'}`}> <Icon size={24} className={active ? 'drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]' : ''} /> <span className="text-[10px] mt-1 font-medium">{label}</span> </button>); }

const voteLoan = async (roomId, reqId, val, uid, userId, runTx) => {
  runTx((t, d) => {
    const ls = [...d.bank.loans];
    const i = ls.findIndex(l => l.requesterId === reqId);
    if (i === -1) return;
    ls[i].votes[uid] = val;
    const seated = d.players.filter(p => p);
    const vs = Object.values(ls[i].votes);
    const y = vs.filter(v => v).length;
    const n = vs.filter(v => !v).length;
    const th = Math.floor(seated.length / 2) + 1;
    let log = null;
    if (y >= th) {
      const pIdx = d.players.findIndex(x => x && x.uid === reqId);
      if (pIdx !== -1) {
        const ps = [...d.players];
        const ledger = d.ledger || {};
        const history = d.bank.history || [];

        ps[pIdx].chips += ls[i].amount;
        if (ledger[reqId]) {
          ledger[reqId].debt = (ledger[reqId].debt || 0) + ls[i].amount;
          ledger[reqId].chips = ps[pIdx].chips;
        }

        log = `批准 ${ls[i].requesterName} 借款`;
        const approvedLoan = ls[i];
        ls.splice(i, 1);

        t.update(null, { 'bank.loans': ls, 'bank.history': [...history, { name: approvedLoan.requesterName, amount: approvedLoan.amount, type: 'approve', ts: Date.now() }], players: ps, ledger, ...(log ? { logs: [...(d.logs || []), log] } : {}) });
        return;
      }
    } else if (n >= th) {
      log = `拒绝 ${ls[i].requesterName}`;
      ls.splice(i, 1);
    }
    t.update(null, { 'bank.loans': ls, ...(log ? { logs: [...(d.logs || []), log] } : {}) });
  });
};